import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
import { availableEdiExportMappers, availableEdiLoaderExportMappers } from '../../helpers/options';
// features
import { socketLBConfigStateReceived } from '../sockets/actions';
// feature configs
import * as A from './actions';
import { getConfigsForCloConfigSuccess } from './config-pages/clo/actions';
import { getBranchConfigsForRatingSuccess } from './config-pages/rating/actions';
import { getGeneralConfigsForTelConfigSuccess } from './config-pages/tel/actions';
import { getConfigsForInvoiceConfigSuccess } from './config-pages/invoice/actions';
import {
  setRefTypesToConfig,
  setSequencesToConfig,
  getDeactivationRuleIndexFromStore,
  setInvoiceStatusToConfigStoreByInvoiceName,
  removeInvoiceStatusToConfigStoreByInvoiceName,
} from './helpers';
//////////////////////////////////////////////////

const defaultInvoiceScopes = {
  [GC.INVOICE_SCOPE_TYPE_DRIVER]: [],
  [GC.INVOICE_SCOPE_TYPE_VENDOR]: [],
  [GC.INVOICE_SCOPE_TYPE_CARRIER]: [],
  [GC.INVOICE_SCOPE_TYPE_CUSTOMER]: [],
  [GC.INVOICE_SCOPE_TYPE_SERVICE_VENDOR]: [],
};

const initialState = {
  currentGroupName: null,
  financial: {
    quickbooks: null,
  },
  activeGroupWithTab: {
    tab: null,
    group: null,
  },
  [GC.CLO_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    guidsToItemsMap: {},
    termsAndConditionsDocument: [],
    defaultOrderAccessorialList: {},
    options: {
      sequences: [],
      accessorials: [],
      serviceTypes: [],
      equipmentType: [],
      orderAccessorials: [],
      cloReferenceTypes: [],
      telReferenceTypes: [],
      allStatusCodeList: [],
      transportationModes: [],
      accessorialConfigList: {},
      availableDocumentTypes: [],
    },
  },
  [GC.TEL_CONFIG_GROUP]: {
    initialValues: {},
    inheritedValues: {},
    guidsToItemsMap: {},
    defaultCarrierAccessorialList: {},
    options: {
      carriers: [],
      sequences: [],
      serviceTypes: [],
      cloReferenceTypes: [],
      telReferenceTypes: [],
      crossDockLocations: [],
      noFuelAccessorials: [],
      transportationModes: [],
      accessorialConfigList: {},
      availableDocumentTypes: [],
      dropCompletedStatusCodes: [],
      pickupCompletedStatusCodes: [],
    },
  },
  [GC.GENERAL_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    guidsToItemsMap: {},
    accessorialsList: [],
    termsAndConditionsDocument: [],
    loadCustomStatusConfigList: {},
    transportationModeGroupingList: [],
    transportationModeConfigMappingList: {},
    options: {
      sequences: [],
      branchSequences: [],
      distanceCalculatorConfigOptions: [],
    },
  },
  [GC.OPTIONAL_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    guidsToItemsMap: {},
    options: {
      customerInvoiceReferenceTypes: [],
    },
  },
  [GC.PASSWORD_CONFIG_GROUP]: {
    initialValues: {},
    inheritedValues: {},
  },
  [GC.TEMPLATES_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    options: {
      sequences: [],
      availableLocationTypes: [],
    },
  },
  [GC.FLEET_GENERAL_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    options: {
      sequences: [],
    },
  },
  [GC.DRIVER_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    onboardingIntegrationList: [],
    termsAndConditionsDocument: [],
    options: {
      gpsStatusCodeList: [],
      dropCompletedStatusCodes: [],
      pickupCompletedStatusCodes: [],
      checkedInStatusCodesForDrop: [],
      checkedInStatusCodesForPickup: [],
    },
  },
  [GC.TRUCK_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
  },
  [GC.TRAILER_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
  },
  [GC.COMMUNICATION_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    statusMessages: [],
    dataSourceList: [],
    inheritedValues: {},
    documentsConfig: [],
    documentsFilter: '',
    notificationList: [],
    mailTemplateByKey: {},
    mailTemplateTypes: [],
    mailTemplateNames: [],
    notificationConfig: [],
    mailTemplateConfig: [],
    documentSendingList: [],
    integrationMailConfig: [],
    indexedDataSourceList: {},
    availableDocumentTypes: [],
    qbDesktopConnectionList: [],
    notificationTriggersFilter: '',
    notificationObjectTypeFilter: '',
    options: {
      orderType: [],
      cloReferenceTypes: [],
      telReferenceTypes: [],
      customerBranchList: [],
    },
  },
  [GC.INVOICE_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    guidsToItemsMap: {},
    expenseTypeList: {},
    glCodeMappingFilter: '',
    qbTaxCodeMappingList: [],
    driverPayrollStatuses: [],
    customerIdMappingList: [],
    carrierInvoiceStatuses: [],
    qbAccountMappingFilter: '',
    customerInvoiceStatuses: [],
    invoiceStatusRuleFilter: '',
    departmentIdMappingList: [],
    sageAccountMappingFilter: '',
    qbCustomFieldMappingList: [],
    termsAndConditionsDocument: [],
    fleetServiceInvoiceStatuses: [],
    qbIffAssessorialMappingFilter: '',
    fuelCardTransactionMappingList: [],
    paylocityCodeMappingList: [],
    propelHRAssessorialMappingList: [],
    glCodeMappingList: defaultInvoiceScopes,
    invoiceStatusRuleList: defaultInvoiceScopes,
    qbIffAssessorialMappingList: {
      [ENUMS.ENUM_DRIVER_PAYROLL]: [],
      [ENUMS.ENUM_VENDOR_PAYROLL]: [],
    },
    sageAccountMappingList: {
      [GC.INVOICE_SCOPE_TYPE_CARRIER]: [],
      [GC.INVOICE_SCOPE_TYPE_CUSTOMER]: [],
    },
    qbAccountMappingList: {
      ...defaultInvoiceScopes,
      [GC.SCOPE_FUEL_CARD_TRANSACTION]: [],
      [GC.INVOICE_SCOPE_TYPE_FLEET_SERVICE]: [],
    },
    options: {
      sequences: [],
      orderType: [],
      templates: [],
      accessorialList: [],
      cloReferenceTypes: [],
      telReferenceTypes: [],
      statusMessageCodes: [],
      customerBranchList: [],
      transportationModes: [],
      integrationSequences: [],
      driverPayrollStatuses: [],
      availableDocumentTypes: [],
      carrierInvoiceStatuses: [],
      customerInvoiceStatuses: [],
      fuelRelatedAccessorialList: [],
      fleetServiceInvoiceStatuses: [],
      nonFuelRelatedAccessorialList: [],
      availableCarrierDocumentTypes: [],
    },
  },
  [GC.RATE_ENGINE_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    marginViolationRuleList: [],
    options: {
      transportationModes: [],
    },
  },
  [GC.SERVICE_VENDOR_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    options: {
      sequences: [],
    },
  },
  [GC.UI_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    geofencingZoneColorConfig: null,
    cloDispatchBoardActionPanel: null,
    telDispatchBoardActionPanel: null,
    centralizedDispatchActionPanel: null,
    options: {
      truckTypes: [],
      cloReferenceTypes: [],
      telReferenceTypes: [],
    },
  },
  [GC.CARRIER_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    deactivationRuleList: [],
    onboardingPackageList: [],
    termsAndConditionsDocument: [],
    carrierScorecardConfigList: [],
    onboardingPackageDocumentList: {},
    options: {
      parentBranches: [],
      telReferenceTypes: [],
      transportationModes: [],
      availableDocumentTypes: [],
    },
  },
  [GC.INTEGRATION_CONFIG_GROUP]: {
    dropdowns: {},
    fuelCardList: [],
    initialValues: {},
    ediConfigsList: [],
    tollChargeList: {},
    inheritedValues: {},
    publicCLOConfig: [],
    gpsIntegrationList: {},
    advancePaymentList: {},
    serviceMappingList: {},
    losPricingPlanList: {},
    loadBoardConfigList: [],
    serviceMappingFilter: '',
    accountingConfigList: {},
    truckTypeMappingList: {},
    losSubscriptionConfig: {},
    integrationConfigList: [],
    statusCodeMappingList: {},
    ediExporterConfigList: [],
    documentHubConfigList: {},
    serviceTypeMappingList: {},
    carrierSynchronization: [],
    ediExtractorConfigList: [],
    trailerTypeMappingList: {},
    documentTypeMappingList: {},
    dispatchIntegrationList: {},
    terminalIntegrationList: {},
    freightSafetyConfigList: {},
    carrierPickupRequesterList: {},
    carrierEdiLoaderConfigList: {},
    carrierRateIntegrationList: {},
    equipmentMappingConfigList: {},
    statusReasonCodeMappingList: {},
    containerTrackingConfigList: {},
    carrierEdiExporterConfigList: {},
    userMonitoringIntegrationList: {},
    statusCodeOutboundMappingList: {},
    factoringIntegrationConfigList: {},
    carrierIntegrationOverrideList: {},
    trailerTrackingIntegrationList: {},
    outboundAccessorialMappingList: {},
    inboundMappingReferenceTypeList: {},
    outboundDocumentTypeMappingList: {},
    outboundMappingReferenceTypeList: {},
    crossBorderIntegrationConfigList: {},
    statusReasonCodeOutboundMappingList: {},
    transportationModeConfigMappingList: [],
    options: {
      carriers: [],
      statusCodeList: [],
      carrierSequences: [],
      allStatusCodeList: [],
      cloReferenceTypes: [],
      telReferenceTypes: [],
      integrationConfigs: [],
      customerBranchList: [],
      [GC.TRUCK_TRUCK_TYPE]: [],
      [GC.GENERAL_SERVICES]: [],
      trackingStatusCodeList: [],
      [GC.GENERAL_EQUIPMENTS]: [],
      accessorialServiceCodes: [],
      [GC.TRAILER_TRAILER_TYPE]: [],
      availableEdiExportMappers: [],
      customerInvoiceReferenceTypes: [],
      [GC.COMMUNICATION_REASON_CODE]: [],
      [GC.COMMUNICATION_DOCUMENT_TYPE]: [],
      [GC.GENERAL_MODE_TRANSPORTATION]: [],
      allStatusCodeListWithStoredValues: [],
      availableEdiExportMappersForLoader: [],
      availableCarrierPickupRequesterList: [],
      [GC.GENERAL_TRANSPORTATION_SERVICE_TYPE]: [],
    },
  },
  [GC.MOBILEAPP_CONFIG_GROUP]: {
    warning: {},
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    options: {
      telReferenceTypes: [],
      availableDocumentTypes: [],
      trackingStatusMessageCodes: [],
      preTripInspectionTemplates: [],
      postTripInspectionTemplates: [],
      dropCompletedStatusMessageCodes: [],
      dropCheckedInStatusMessageCodes: [],
      pickupCompletedStatusMessageCodes: [],
      pickupCheckedInStatusMessageCodes: [],
      checkedInCompletedStatusMessageCodes: [],
    },
  },
  [GC.TASK_MANAGEMENT_CONFIG_GROUP]: {
    dropdowns: {},
    taskTypeList: {},
    initialValues: {},
    taskStatusList: {},
    inheritedValues: {},
    options: {
      sequences: [],
    },
  },

  [GC.CLAIM_CONFIG_GROUP]: {
    dropdowns: {},
    initialValues: {},
    inheritedValues: {},
    options: {
      sequences: [],
    },
  },
};

// configs for
const handleGetConfigsForCloConfigSuccess = (state, data) => (
  P.$all(
    P.$set(
      `${GC.CLO_CONFIG_GROUP}.options.serviceTypes`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.GENERAL_TRANSPORTATION_SERVICE_TYPE),
    ),
    P.$set(
      `${GC.CLO_CONFIG_GROUP}.options.transportationModes`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.GENERAL_MODE_TRANSPORTATION),
    ),
    P.$set(
      `${GC.CLO_CONFIG_GROUP}.options.equipmentType`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.GENERAL_EQUIPMENTS),
    ),
    P.$set(
      `${GC.CLO_CONFIG_GROUP}.options.documentType`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.COMMUNICATION_DOCUMENT_TYPE),
    ),
    P.$set(
      `${GC.CLO_CONFIG_GROUP}.options.locationType`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.TEMPLATES_LOCATION_TYPE),
    ),
    state,
  )
);

const handleGetGeneralConfigsForTelConfigSuccess = (state, data) => (
  P.$all(
    P.$set(
      `${GC.TEL_CONFIG_GROUP}.options.serviceTypes`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.GENERAL_TRANSPORTATION_SERVICE_TYPE),
    ),
    P.$set(
      `${GC.TEL_CONFIG_GROUP}.options.transportationModes`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.GENERAL_MODE_TRANSPORTATION),
    ),
    state,
  )
);

const handleGetConfigsForInvoiceConfigSuccess = (state, data) => (
  P.$all(
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.orderType`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.CLO_ORDER_TYPE_DROPDOWN),
    ),
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.transportationModes`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.GENERAL_MODE_TRANSPORTATION),
    ),
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.availableCarrierDocumentTypes`,
      G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.CARRIER_DOCUMENT_TYPE),
    ),
    state,
  )
);

const getConfigsForCommunicationConfigSuccess = (state, data) => (
  P.$set(
    `${GC.COMMUNICATION_CONFIG_GROUP}.options.orderType`,
    G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.CLO_ORDER_TYPE_DROPDOWN),
    state,
  )
);

const handleGetBranchConfigsForRatingSuccess = (state, data) => (
  P.$set(
    `${GC.RATE_ENGINE_CONFIG_GROUP}.options.transportationModes`,
    G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.GENERAL_MODE_TRANSPORTATION),
    state,
  )
);

const handleGetConfigsForUiConfigSuccess = (state, data) => (
  P.$set(
    `${GC.UI_CONFIG_GROUP}.options.truckTypes`,
    G.createOptionsFromDropdownConfigWithGuidOrParentGuid(data, GC.TRUCK_TRUCK_TYPE),
    state,
  )
);
//

const setActiveConfigTab = (state, tab) => {
  const { currentGroupName } = state;

  return P.$set(`${currentGroupName}.activeTab`, tab, state);
};

const setActiveConfigGroupWithTab = (state, groupWithTab) => {
  return P.$set('activeGroupWithTab', groupWithTab, state);
};

const setConfigTabsVisited = (state, tabsVisited) => {
  const { currentGroupName } = state;

  return P.$set(`${currentGroupName}.tabsVisited`, tabsVisited, state);
};

const getConfigByGroupSuccess = (state, data) => {
  const { res, groupName} = data;

  const { configs, dropdowns } = res;

  let initialValues = {};
  let inheritedValues = {};

  R.forEach((item) => {
    initialValues = R.assoc(item.name, item.value, initialValues);
    inheritedValues = R.assoc(item.name, item.inheritedValue, inheritedValues);
  }, configs);

  return P.$all(
    P.$set('currentGroupName', groupName),
    P.$set(`${groupName}.initialValues`, initialValues),
    P.$set(`${groupName}.inheritedValues`, inheritedValues),
    P.$set(`${groupName}.dropdowns`, R.indexBy(R.prop('name'), dropdowns)),
    state,
  );
};

const getGroupConfigsByNamesSuccess = (state, { data, groupName }) => {
  const { configs, dropdowns } = data;

  let initialValues = P.$get(`${groupName}.initialValues`, state);
  let inheritedValues = P.$get(`${groupName}.inheritedValues`, state);

  R.forEach((item) => {
    initialValues = R.assoc(item.name, item.value, initialValues);
    inheritedValues = R.assoc(item.name, item.inheritedValue, inheritedValues);
  }, configs);

  const currentDropdowns = P.$get(`${groupName}.dropdowns`, state);
  const updatedDropdowns = {
    ...currentDropdowns,
    ...R.indexBy(R.prop('name'), dropdowns),
  };

  return P.$all(
    P.$set(`${groupName}.dropdowns`, updatedDropdowns),
    P.$set(`${groupName}.initialValues`, initialValues),
    P.$set(`${groupName}.inheritedValues`, inheritedValues),
    state,
  );
};

const createOrUpdateDropdownOptionConfigSuccess = (state, data) => {
  const { currentGroupName } = state;

  const { guid, dropdownName } = data;

  const path = `${currentGroupName}.dropdowns.${dropdownName}.options`;
  const options = R.pathOr([], R.split('.', path), state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), options);

  if (R.isEmpty(options)) P.$set(path, R.of(data), state);

  if (R.equals(index, -1)) return P.$add(path, data, state);

  return P.$set(`${path}.${index}`, data, state);
};

const removeDropdownOptionConfigSuccess = (state, data) => {
  const { currentGroupName } = state;

  const { guid, dropdownName } = data;

  const path = `${currentGroupName}.dropdowns.${dropdownName}.options`;
  const options = R.path(R.split('.', path), state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), options);

  return P.$drop(`${path}.${index}`, state);
};

const getReferenceTypesGuidsNamesMapSuccess = (state, data) => (
  P.$all(
    P.$merge(`${GC.CLO_CONFIG_GROUP}.guidsToItemsMap`, data),
    P.$merge(`${GC.TEL_CONFIG_GROUP}.guidsToItemsMap`, data),
    P.$merge(`${GC.OPTIONAL_CONFIG_GROUP}.guidsToItemsMap`, data),
    state,
  )
);

const getSequencesGuidNameMapSuccess = (state, data) => (
  P.$all(
    P.$merge(`${GC.CLO_CONFIG_GROUP}.guidsToItemsMap`, data),
    P.$merge(`${GC.TEL_CONFIG_GROUP}.guidsToItemsMap`, data),
    P.$merge(`${GC.GENERAL_CONFIG_GROUP}.guidsToItemsMap`, data),
    P.$merge(`${GC.INVOICE_CONFIG_GROUP}.guidsToItemsMap`, data),
    state,
  )
);

const getReferenceTypesByScopeForConfigSuccess = (state, data) => (
  setRefTypesToConfig(state, data)
);

const getSequencesByTypeForConfigSuccess = (state, data) => (
  setSequencesToConfig(state, data)
);

const mapAccessorialsToOptions = (data) => R.map(
  (item) => ({ label: R.prop(GC.FIELD_DISPLAYED_VALUE, item), value: R.prop(GC.FIELD_ORIGINAL_CONFIG_GUID, item) }),
  data,
);

const getAccessorialForConfigSuccess = (state, data) => (
  P.$all(
    P.$set(`${GC.GENERAL_CONFIG_GROUP}.accessorialsList`, data),
    P.$set(`${GC.INVOICE_CONFIG_GROUP}.options.accessorialList`, data),
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.accessorialConfigList`, mapAccessorialsToOptions(data)),
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.fuelRelatedAccessorialList`,
      mapAccessorialsToOptions(R.filter(R.propEq(true, GC.FIELD_FUEL_RELATED), data)),
    ),
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.nonFuelRelatedAccessorialList`,
      mapAccessorialsToOptions(R.filter(R.propEq(false, GC.FIELD_FUEL_RELATED), data)),
    ),
    state,
  )
);

const getBranchConfigsByNamesForCarrierGroupSuccess = (state, data) => {
  const mappedConfigs = G.mapConfigValuesByName(data);

  const transportationModeOptions = G.createOptionsFromDropdownConfigWithGuidOrParentGuid(
    mappedConfigs,
    GC.GENERAL_MODE_TRANSPORTATION,
  );

  return P.$set(`${GC.CARRIER_CONFIG_GROUP}.options.transportationModes`, transportationModeOptions, state);
};

const getCarrierDeactivationRuleListSuccess = (state, data) => (
  P.$set(
    `${GC.CARRIER_CONFIG_GROUP}.deactivationRuleList`,
    R.map((entity) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, entity),
      entity,
    ), data),
    state,
  )
);

const getParentBranchesByBranchGuidSuccess = (state, data) => (
  P.$set(`${GC.CARRIER_CONFIG_GROUP}.options.parentBranches`, data, state)
);

const createCarrierDeactivationRuleSuccess = (state, rule) => (
  P.$add(`${GC.CARRIER_CONFIG_GROUP}.deactivationRuleList`, rule, state)
);

const updateCarrierDeactivationRuleSuccess = (state, data) => (
  P.$set(
    `${GC.CARRIER_CONFIG_GROUP}.deactivationRuleList.${getDeactivationRuleIndexFromStore(data.guid, state)}`,
    data,
    state,
  )
);

const deleteCarrierDeactivationRuleSuccess = (state, guid) => (
  P.$drop(`${GC.CARRIER_CONFIG_GROUP}.deactivationRuleList.${getDeactivationRuleIndexFromStore(guid, state)}`, state)
);

const createAccessorialSuccess = (state, data) => (
  P.$add(`${GC.GENERAL_CONFIG_GROUP}.accessorialsList`, data, state)
);

const updateAccessorialSuccess = (state, data) => {
  const stateAccessorials = P.$get(`${GC.GENERAL_CONFIG_GROUP}.accessorialsList`, state);
  const mappedAccessorials = stateAccessorials.map((item) => (
    G.ifElse(
      R.equals(item.guid, data.guid),
      data,
      item,
    )
  ));
  return P.$set(`${GC.GENERAL_CONFIG_GROUP}.accessorialsList`, mappedAccessorials, state);
};

const deleteAccessorialSuccess = (state, guid) => {
  const filter = (item) => G.notContain(item.guid, guid);
  const accessorials = R.filter(filter, state[GC.GENERAL_CONFIG_GROUP].accessorialsList);
  return P.$set(`${GC.GENERAL_CONFIG_GROUP}.accessorialsList`, accessorials, state);
};

const getCustomerInvoiceStatusesSuccess = (state, data) => {
  const options = R.map((item) => ({
    ...item,
    [GC.FIELD_LABEL]: G.getDisplayedValueFromObject(item),
    [GC.FIELD_VALUE]: G.getParentGuidOrGuidFromObject(item),
  }), data);

  return (
    P.$all(
      P.$set(`${GC.INVOICE_CONFIG_GROUP}.customerInvoiceStatuses`, data),
      P.$set(`${GC.INVOICE_CONFIG_GROUP}.options.customerInvoiceStatuses`, options),
      P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.customerInvoiceStatuses`, options),
      state,
    )
  );
};

const createCustomerInvoiceStatusesSuccess = (state, data) =>
  setInvoiceStatusToConfigStoreByInvoiceName(state, data, 'customerInvoiceStatuses');

const updateCustomerInvoiceStatusSuccess = (state, data) =>
  setInvoiceStatusToConfigStoreByInvoiceName(state, data, 'customerInvoiceStatuses');

const deleteCustomerInvoiceStatusSuccess = (state, guid) =>
  removeInvoiceStatusToConfigStoreByInvoiceName(state, guid, 'customerInvoiceStatuses');

const getCarrierInvoiceStatusesSuccess = (state, data) => (
  P.$all(
    P.$set(`${GC.INVOICE_CONFIG_GROUP}.carrierInvoiceStatuses`, data),
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.carrierInvoiceStatuses`,
      R.map((item) => R.mergeRight(
        item,
        {
          [GC.FIELD_VALUE]: G.getParentGuidOrGuidFromObject(item),
          [GC.FIELD_LABEL]: G.getPropFromObject(GC.FIELD_DISPLAYED_VALUE, item),
        },
      ), data),
    ),
    state,
  )
);

const createCarrierInvoiceStatusSuccess = (state, data) =>
  setInvoiceStatusToConfigStoreByInvoiceName(state, data, 'carrierInvoiceStatuses');

const updateCarrierInvoiceStatusSuccess = (state, data) =>
  setInvoiceStatusToConfigStoreByInvoiceName(state, data, 'carrierInvoiceStatuses');

const deleteCarrierInvoiceStatusSuccess = (state, guid) =>
  removeInvoiceStatusToConfigStoreByInvoiceName(state, guid, 'carrierInvoiceStatuses');

const getFleetServiceInvoiceStatusesSuccess = (state, data) => (
  P.$all(
    P.$set(`${GC.INVOICE_CONFIG_GROUP}.fleetServiceInvoiceStatuses`, data),
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.fleetServiceInvoiceStatuses`,
      R.map((item) => R.mergeRight(
        item,
        {
          [GC.FIELD_LABEL]: G.getPropFromObject(GC.FIELD_DISPLAYED_VALUE, item),
          [GC.FIELD_VALUE]: G.getPropFromObject(GC.FIELD_ORIGINAL_CONFIG_GUID, item),
        },
      ), data),
    ),
    state,
  )
);

const createOrUpdateFleetServiceInvoiceStatusSuccess = (state, data) =>
  setInvoiceStatusToConfigStoreByInvoiceName(state, data, 'fleetServiceInvoiceStatuses');

const deleteFleetServiceInvoiceStatusSuccess = (state, guid) =>
  removeInvoiceStatusToConfigStoreByInvoiceName(state, guid, 'fleetServiceInvoiceStatuses');

const getDriverPayrollStatusesSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const mapped = data.map((obj) => ({
    ...obj,
    label: obj.displayedValue,
    value: G.getParentGuidOrGuidFromObject(obj),
  }));

  return P.$all(
    P.$set(`${GC.INVOICE_CONFIG_GROUP}.driverPayrollStatuses`, data),
    P.$set(`${GC.INVOICE_CONFIG_GROUP}.options.driverPayrollStatuses`, mapped),
    P.$set(`${GC.MOBILEAPP_CONFIG_GROUP}.options.driverPayrollStatuses`, mapped),
    state,
  );
};

const getAvailableInspectionTemplatesSuccess = (state, data) => P.$all(
  P.$set(
    `${GC.MOBILEAPP_CONFIG_GROUP}.options.preTripInspectionTemplates`,
    R.compose(
      R.map(({ name, guid }) => ({ label: name, value: guid })),
      R.filter(R.propEq(ENUMS.ENUM_INSPECTION_TYPE_PRE_TRIP, GC.FIELD_TYPE)),
    )(data),
  ),
  P.$set(
    `${GC.MOBILEAPP_CONFIG_GROUP}.options.postTripInspectionTemplates`,
    R.compose(
      R.map(({ name, guid }) => ({ label: name, value: guid })),
      R.filter(R.propEq(ENUMS.ENUM_INSPECTION_TYPE_POST_TRIP, GC.FIELD_TYPE)),
    )(data),
  ),
  state,
);

const createDriverPayrollStatusSuccess = (state, data) =>
  setInvoiceStatusToConfigStoreByInvoiceName(state, data, 'driverPayrollStatuses');

const updateDriverPayrollStatusSuccess = (state, data) =>
  setInvoiceStatusToConfigStoreByInvoiceName(state, data, 'driverPayrollStatuses');

const deleteDriverPayrollStatusSuccess = (state, guid) =>
  removeInvoiceStatusToConfigStoreByInvoiceName(state, guid, 'driverPayrollStatuses');

const getStatusMessagesForConfigSuccess = (state, data) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.statusMessages`, data, state)
);

const getDocumentsForConfigSuccess = (state, data) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.documentsConfig`, data, state)
);

const createStatusMessageSuccess = (state, data) => (
  P.$add(`${GC.COMMUNICATION_CONFIG_GROUP}.statusMessages`, data, state)
);

const createDocumentSuccess = (state, data) => (
  P.$add(`${GC.COMMUNICATION_CONFIG_GROUP}.documentsConfig`, data, state)
);

const setDocumentsFilter = (state, filter) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.documentsFilter`, filter, state)
);

const createNotificationSuccess = (state, data) => (
  P.$add(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationList`, data, state)
);

const updateStatusMessageSuccess = (state, data) => {
  const statuses = P.$get(`${GC.COMMUNICATION_CONFIG_GROUP}.statusMessages`, state);
  const mappedStatuses = statuses.map((item) => (
    G.ifElse(
      R.equals(item.guid, data.guid),
      data,
      item,
    )
  ));
  return P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.statusMessages`, mappedStatuses, state);
};

const updateDocumentSuccess = (state, data) => {
  const docs = P.$get(`${GC.COMMUNICATION_CONFIG_GROUP}.documentsConfig`, state);
  const documents = docs.map((item) => (
    G.ifElse(
      R.equals(item.guid, data.guid),
      data,
      item,
    )
  ));
  return P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.documentsConfig`, documents, state);
};

const updateNotificationConfigsSuccess = (state, data) => {
  const docs = P.$get(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationList`, state);
  const notification = docs.map((item) => (
    G.ifElse(
      R.equals(item.guid, data.guid),
      data,
      item,
    )
  ));
  return P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationList`, notification, state);
};

const deleteStatusMessageSuccess = (state, guid) => {
  const filter = (item) => G.notContain(item.guid, guid);
  const statusMessages = R.filter(
    filter,
    state[GC.COMMUNICATION_CONFIG_GROUP].statusMessages,
  );
  return P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.statusMessages`, statusMessages, state);
};

const deleteDocumentConfigSuccess = (state, guid) => {
  const filter = (item) => G.notContain(item.guid, guid);
  const documents = R.filter(
    filter,
    state[GC.COMMUNICATION_CONFIG_GROUP].documentsConfig,
  );
  return P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.documentsConfig`, documents, state);
};

const deleteNotificationSuccess = (state, guid) => {
  const filter = (item) => G.notContain(item.guid, guid);
  const notification = R.filter(
    filter,
    state[GC.COMMUNICATION_CONFIG_GROUP].notificationList,
  );
  return P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationList`, notification, state);
};

const connectToFinancialSuccess = (state, data) => (
  P.$set(`financial.${data.type}`, data, state)
);

const getNotificationTriggersSuccess = (state, data) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationConfig`, data, state)
);

const getNotificationListTriggersSuccess = (state, data) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationList`, data, state)
);

const setNotificationTriggersFilter = (state, filter) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationTriggersFilter`, filter, state)
);

const setNotificationObjectTypeFilter = (state, filter) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.notificationObjectTypeFilter`, filter, state)
);

const getStatusCodeListSuccess = (
  state,
  {
    statusCodeList,
    allStatusCodeList,
    gpsStatusCodeList,
    trackingStatusCodeList,
    stopCheckedInStatusCodes,
    dropCompletedStatusCodes,
    dropCheckedInStatusCodes,
    pickupCompletedStatusCodes,
    pickupCheckedInStatusCodes,
    checkedInStatusCodesForDrop,
    checkedInStatusCodesForPickup,
    allStatusCodeListWithStoredValues,
  },
) => (
  P.$all(
    P.$set(`${GC.CLO_CONFIG_GROUP}.options.allStatusCodeList`, allStatusCodeList),
    //
    P.$set(`${GC.TEL_CONFIG_GROUP}.options.dropCompletedStatusCodes`, dropCompletedStatusCodes),
    P.$set(`${GC.TEL_CONFIG_GROUP}.options.pickupCompletedStatusCodes`, pickupCompletedStatusCodes),
    //
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.statusCodeList`, statusCodeList),
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.allStatusCodeList`, allStatusCodeList),
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.trackingStatusCodeList`, trackingStatusCodeList),
    P.$set(
      `${GC.INTEGRATION_CONFIG_GROUP}.options.allStatusCodeListWithStoredValues`,
      allStatusCodeListWithStoredValues,
    ),
    //
    P.$set(`${GC.DRIVER_CONFIG_GROUP}.options.gpsStatusCodeList`, gpsStatusCodeList),
    P.$set(`${GC.DRIVER_CONFIG_GROUP}.options.dropCompletedStatusCodes`, dropCompletedStatusCodes),
    P.$set(`${GC.DRIVER_CONFIG_GROUP}.options.pickupCompletedStatusCodes`, pickupCompletedStatusCodes),
    P.$set(`${GC.DRIVER_CONFIG_GROUP}.options.checkedInStatusCodesForDrop`, checkedInStatusCodesForDrop),
    P.$set(`${GC.DRIVER_CONFIG_GROUP}.options.checkedInStatusCodesForPickup`, checkedInStatusCodesForPickup),
    //
    P.$set(`${GC.MOBILEAPP_CONFIG_GROUP}.options.trackingStatusMessageCodes`, trackingStatusCodeList),
    P.$set(`${GC.MOBILEAPP_CONFIG_GROUP}.options.dropCompletedStatusMessageCodes`, dropCompletedStatusCodes),
    P.$set(`${GC.MOBILEAPP_CONFIG_GROUP}.options.dropCheckedInStatusMessageCodes`, dropCheckedInStatusCodes),
    P.$set(`${GC.MOBILEAPP_CONFIG_GROUP}.options.pickupCompletedStatusMessageCodes`, pickupCompletedStatusCodes),
    P.$set(`${GC.MOBILEAPP_CONFIG_GROUP}.options.pickupCheckedInStatusMessageCodes`, pickupCheckedInStatusCodes),
    P.$set(`${GC.MOBILEAPP_CONFIG_GROUP}.options.checkedInCompletedStatusMessageCodes`, stopCheckedInStatusCodes),
    state,
  )
);

const receivedBranchConfigsByNameSuccess = (state, data) => (
  P.$merge(`${GC.INTEGRATION_CONFIG_GROUP}.options`, data, state)
);

const receivedAccessorialServiceCodesSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.accessorialServiceCodes`, data, state)
);

const getFuelCardListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.fuelCardList`,
    R.indexBy(R.prop(GC.FIELD_GUID), data),
    state,
  )
);

const getCustomerBranchListSuccess = (state, data) => (
  P.$all(
    P.$set(`${GC.INVOICE_CONFIG_GROUP}.options.customerBranchList`, data),
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.customerBranchList`, data),
    P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.options.customerBranchList`, data),
    state,
  )
);

const receivedFuelCardSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.fuelCardList.${data.guid}`, data, state)
);

const receivedCarrierRateIntegrationListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.carrierRateIntegrationList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateCarrierRateIntegrationSuccess = (state, data) => {
  const { guid } = data;
  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'carrierRateIntegrationList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);
  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.carrierRateIntegrationList`, newList, state);
};

const removeCarrierRateIntegrationSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.carrierRateIntegrationList.${guid}`, state)
);

const receivedUserMonitoringIntegrationListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.userMonitoringIntegrationList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateUserMonitoringIntegrationSuccess = (state, data) => {
  const { guid } = data;
  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'userMonitoringIntegrationList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);
  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.userMonitoringIntegrationList`, newList, state);
};

const removeUserMonitoringIntegrationSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.userMonitoringIntegrationList.${guid}`, state)
);

const getServiceMappingListSuccess = (state, data) => {
  const serviceMappingList = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.serviceMappingList`, serviceMappingList, state);
};

const setServiceMappingFilter = (state, filter) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.serviceMappingFilter`, filter, state)
);

const createServiceMappingListSuccess = (state, list) => {
  const { serviceMappingList } = state;

  const length = R.length(R.values(serviceMappingList));
  const indexedList = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      {
        index: R.add(index, length),
        editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
      },
    )),
  )(list);

  return P.$merge(`${GC.INTEGRATION_CONFIG_GROUP}.serviceMappingList`, indexedList, state);
};

const createOrUpdateServiceMappingSuccess = (state, data) => {
  const { guid } = data;
  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'serviceMappingList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.serviceMappingList`, newList, state);
};

const removeServiceMappingSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.serviceMappingList.${guid}`, state)
);

const getAdvancePaymentListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      {
        index,
        editable: R.and(
          R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
          R.not(R.propEq(
            GC.ADVANCE_PAYMENT_INTEGRATION_TYPE_RELAY,
            GC.FIELD_CONFIGURATION_INTEGRATION_ADVANCE_PAYMENT_INTEGRATION_TYPE,
            item,
          )),
        ),
      },
    )),
  )(data);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.advancePaymentList`, list, state);
};

const createOrUpdateAdvancePaymentSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'advancePaymentList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);

  const editable = R.not(R.propEq(
    GC.ADVANCE_PAYMENT_INTEGRATION_TYPE_RELAY,
    GC.FIELD_CONFIGURATION_INTEGRATION_ADVANCE_PAYMENT_INTEGRATION_TYPE,
    data,
  ));

  const newListItem = { ...data, index, editable };
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.advancePaymentList`, newList, state);
};

const removeAdvancePaymentSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.advancePaymentList.${guid}`, state)
);

const getEdiConfigsListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.ediConfigsList`,
    R.indexBy(R.prop(GC.FIELD_GUID), data),
    state,
  )
);

const receivedEdiConfigsSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.ediConfigsList.${data.guid}`, data, state)
);

const getEdiExporterConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.ediExporterConfigList`,
    R.indexBy(R.prop(GC.FIELD_GUID), data),
    state,
  )
);

const receivedEdiExporterConfigSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.ediExporterConfigList.${G.getGuidFromObject(data)}`, data, state)
);

const getEdiExtractorConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.ediExtractorConfigList`,
    R.indexBy(R.prop(GC.FIELD_GUID), data),
    state,
  )
);

const receivedEdiExtractorConfigSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.ediExtractorConfigList.${G.getGuidFromObject(data)}`, data, state)
);

// public clo
const getPublicCLOConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.publicCLOConfig`, [], state);

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.publicCLOConfig`,
    R.of(Array, R.assoc('editable', R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, data), data)),
    state,
  );
};

const removePublicCLOConfigSuccess = (state) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.publicCLOConfig`, [], state)
);

const mapAvailableDocumentTypes = (typeObj) => ({
  ...typeObj,
  label: G.getDisplayedValueFromObject(typeObj),
  value: G.getParentGuidOrGuidFromObject(typeObj),
});

const receivedAvailableDocumentTypesSuccess = (state, data) => (
  P.$all(
    P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.availableDocumentTypes`, data),
    P.$set(
      `${GC.CLO_CONFIG_GROUP}.options.availableDocumentTypes`,
      data.map((typeObj) => mapAvailableDocumentTypes(typeObj)),
    ),
    P.$set(
      `${GC.INVOICE_CONFIG_GROUP}.options.availableDocumentTypes`,
      data.map((typeObj) => mapAvailableDocumentTypes(typeObj)),
    ),
    P.$set(
      `${GC.TEL_CONFIG_GROUP}.options.availableDocumentTypes`,
      data.map((typeObj) => mapAvailableDocumentTypes(typeObj)),
    ),
    P.$set(
      `${GC.MOBILEAPP_CONFIG_GROUP}.options.availableDocumentTypes`,
      data.map((typeObj) => mapAvailableDocumentTypes(typeObj)),
    ),
    P.$set(
      `${GC.CARRIER_CONFIG_GROUP}.options.availableDocumentTypes`,
      data.map((typeObj) => mapAvailableDocumentTypes(typeObj)),
    ),
    state,
  )
);

const setConfigWarning = (state, { tabName, message, configGroup }) => (
  P.$set(`${configGroup}.warning.${tabName}`, message, state)
);

// qb account mapping
const setQBAccountMappingFilter = (state, filter) => (
  P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbAccountMappingFilter`, filter, state)
);

const getQBAccountMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const groupedList = R.compose(
    R.mergeRight(R.path([GC.INVOICE_CONFIG_GROUP, 'qbAccountMappingList'], initialState)),
    R.groupBy(R.prop(GC.FIELD_SCOPE)),
    R.map((item) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
      item,
    )),
  )(data);

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbAccountMappingList`, groupedList, state);
};

const createOrUpdateQBAccountMappingSuccess = (state, data) => {
  const { guid, scope } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbAccountMappingList', scope], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);
  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.qbAccountMappingList.${scope}`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbAccountMappingList.${scope}.${index}`, data, state);
};

const removeQBAccountMappingSuccess = (state, { scope, guid }) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbAccountMappingList', scope], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.qbAccountMappingList.${scope}.${index}`, state);
};

// qb custom field mapping
const getQBCustomFieldMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbCustomFieldMappingList`, data, state);
};

const createOrUpdateQBCustomFieldMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbCustomFieldMappingList'], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.qbCustomFieldMappingList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbCustomFieldMappingList.${index}`, data, state);
};

const removeQBCustomFieldMappingSuccess = (state, guid) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbCustomFieldMappingList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.qbCustomFieldMappingList.${index}`, state);
};

// qb tax code mapping
const getQBTaxCodeMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbTaxCodeMappingList`, data, state);
};

const createOrUpdateQBTaxCodeMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbTaxCodeMappingList'], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.qbTaxCodeMappingList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbTaxCodeMappingList.${index}`, data, state);
};

const removeQBTaxCodeMappingSuccess = (state, guid) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbTaxCodeMappingList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.qbTaxCodeMappingList.${index}`, state);
};

// qb iif accessorial mapping
const setQbIffAssessorialMappingFilter = (state, filter) => (
  P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbIffAssessorialMappingFilter`, filter, state)
);

const getQBIIFAccessorialMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const groupedList = R.compose(
    R.mergeRight(R.path([GC.INVOICE_CONFIG_GROUP, 'qbIffAssessorialMappingList'], initialState)),
    R.groupBy(R.prop(GC.FIELD_SCOPE)),
    R.map((item) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
      item,
    )),
  )(data);

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbIffAssessorialMappingList`, groupedList, state);
};

const createOrUpdateQBIIFAccessorialMappingSuccess = (state, data) => {
  const { guid, scope } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbIffAssessorialMappingList', scope], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);
  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.qbIffAssessorialMappingList.${scope}`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbIffAssessorialMappingList.${scope}.${index}`, data, state);
};

const removeQBIIFAccessorialMappingSuccess = (state, { scope, guid }) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbIffAssessorialMappingList', scope], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.qbIffAssessorialMappingList.${scope}.${index}`, state);
};

// qb desktop connecting
const getQBDesktopConnectionListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbDesktopConnectionList`, data, state);
};

const createOrUpdateQBDesktopConnectionSuccess = (state, data) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbDesktopConnectionList'], state);

  const { guid } = data;

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.qbDesktopConnectionList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.qbDesktopConnectionList.${index}`, data, state);
};

const removeQBDesktopConnectionSuccess = (state, { guid }) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'qbDesktopConnectionList'], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.qbDesktopConnectionList.${index}`, state);
};

// propel hr assessorial mapping
const getPropelHRAssessorialMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.propelHRAssessorialMappingList`, data, state);
};

const createOrUpdatePropelHRAssessorialMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'propelHRAssessorialMappingList'], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.propelHRAssessorialMappingList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.propelHRAssessorialMappingList.${index}`, data, state);
};

const removePropelHRAssessorialMappingSuccess = (state, guid) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'propelHRAssessorialMappingList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.propelHRAssessorialMappingList.${index}`, state);
};

// paylocity code mapping
const getPaylocityCodeMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.paylocityCodeMappingList`, data, state);
};

const createOrUpdatePaylocityCodeMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'paylocityCodeMappingList'], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.paylocityCodeMappingList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.paylocityCodeMappingList.${index}`, data, state);
};

const removePaylocityCodeMappingSuccess = (state, guid) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'paylocityCodeMappingList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.paylocityCodeMappingList.${index}`, state);
};

// fuel card transaction mapping
const getFuelCardTransactionMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.fuelCardTransactionMappingList`, data, state);
};

const createOrUpdateFuelCardTransactionMappingSuccess = (state, data) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'fuelCardTransactionMappingList'], state);

  const { guid } = data;

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.fuelCardTransactionMappingList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.fuelCardTransactionMappingList.${index}`, data, state);
};

const removeFuelCardTransactionMappingSuccess = (state, guid) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'fuelCardTransactionMappingList'], state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.fuelCardTransactionMappingList.${index}`, state);
};

// customer id mapping
const getCustomerIdMappingListSuccess = (state, data) => (
  P.$set(
    `${GC.INVOICE_CONFIG_GROUP}.customerIdMappingList`,
    R.map((entity) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, entity),
      entity,
    ), data),
    state,
  )
);

const removeCustomerIdMappingSuccess = (state, data) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'customerIdMappingList'], state);
  const index = R.findIndex(R.propEq(data, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.customerIdMappingList.${index}`, state);
};

const createOrUpdateCustomerIdMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'customerIdMappingList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.customerIdMappingList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.customerIdMappingList.${index}`, data, state);
};

// department id mapping
const getDepartmentIdMappingListSuccess = (state, data) => (
  P.$set(
    `${GC.INVOICE_CONFIG_GROUP}.departmentIdMappingList`,
    R.map((entity) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, entity),
      entity,
    ), data),
    state,
  )
);

const removeDepartmentIdMappingSuccess = (state, data) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'departmentIdMappingList'], state);
  const index = R.findIndex(R.propEq(data, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.departmentIdMappingList.${index}`, state);
};

const createOrUpdateDepartmentIdMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'departmentIdMappingList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.departmentIdMappingList`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.departmentIdMappingList.${index}`, data, state);
};

// sage account mapping
const setSageAccountMappingFilter = (state, filter) => (
  P.$set(`${GC.INVOICE_CONFIG_GROUP}.sageAccountMappingFilter`, filter, state)
);

const getSageAccountMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const groupedList = R.compose(
    R.mergeRight(R.path([GC.INVOICE_CONFIG_GROUP, 'sageAccountMappingList'], initialState)),
    R.groupBy(R.prop(GC.FIELD_SCOPE)),
    R.map((item) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
      item,
    )),
  )(data);

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.sageAccountMappingList`, groupedList, state);
};

const createOrUpdateSageAccountMappingSuccess = (state, data) => {
  const { guid, scope } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'sageAccountMappingList', scope], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.sageAccountMappingList.${scope}`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.sageAccountMappingList.${scope}.${index}`, data, state);
};

const removeSageAccountMappingSuccess = (state, { scope, guid }) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'sageAccountMappingList', scope], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.sageAccountMappingList.${scope}.${index}`, state);
};

// gl code mapping
const setGLCodeMappingFilter = (state, filter) => (
  P.$set(`${GC.INVOICE_CONFIG_GROUP}.glCodeMappingFilter`, filter, state)
);

const getGLCodeMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const groupedList = R.compose(
    R.mergeRight(R.path([GC.INVOICE_CONFIG_GROUP, 'glCodeMappingList'], initialState)),
    R.groupBy(R.prop(GC.FIELD_SCOPE)),
    R.map((item) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
      item,
    )),
  )(data);

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.glCodeMappingList`, groupedList, state);
};

const createOrUpdateCLCodeMappingSuccess = (state, data) => {
  const { guid, scope } = data;

  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'glCodeMappingList', scope], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);
  if (R.lt(index, 0)) {
    return P.$add(`${GC.INVOICE_CONFIG_GROUP}.glCodeMappingList.${scope}`, data, state);
  }

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.glCodeMappingList.${scope}.${index}`, data, state);
};

const removeGLCodeMappingSuccess = (state, { guid, scope }) => {
  const list = R.pathOr([], [GC.INVOICE_CONFIG_GROUP, 'glCodeMappingList', scope], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  return P.$drop(`${GC.INVOICE_CONFIG_GROUP}.glCodeMappingList.${scope}.${index}`, state);
};

// invoice status rule
const setInvoiceStatusRuleFilter = (state, filter) => (
  P.$set(`${GC.INVOICE_CONFIG_GROUP}.invoiceStatusRuleFilter`, filter, state)
);

const getInvoiceStatusRuleListSuccess = (state, data) => {
  const groupedList = R.compose(
    R.mergeRight(R.path([GC.INVOICE_CONFIG_GROUP, 'invoiceStatusRuleList'], initialState)),
    R.groupBy(R.prop(GC.FIELD_SCOPE)),
    R.map((item) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
      item,
    )),
  )(data);

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.invoiceStatusRuleList`, groupedList, state);
};

const getAccessorialConfigListForTelConfigSuccess = (state, data) => {
  const noFuelAccessorialOptions = R.compose(
    R.map(({ displayedValue, originalConfigGuid }) => ({
      [GC.FIELD_LABEL]: displayedValue,
      [GC.FIELD_VALUE]: originalConfigGuid,
    })),
    R.filter(({ fuelRelated }) => G.isFalse(fuelRelated)),
  )(data);

  const accessorialConfigList = R.compose(
    R.indexBy(R.prop(GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID)),
    R.map((item) => R.compose(
      R.assoc(GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID, R.prop(GC.FIELD_ORIGINAL_CONFIG_GUID, item)),
      R.pick([
        GC.FIELD_NON_TAXABLE,
        GC.FIELD_FUEL_RELATED,
        GC.FIELD_DISPLAYED_VALUE,
        GC.FIELD_CHARGE_RATE_TYPE,
        GC.FIELD_CHARGE_RATE_UNIT,
        GC.FIELD_CHARGE_INTERNAL_EXPENSE,
      ]),
    )(item)),
  )(data);

  return P.$all(
    P.$set(`${GC.TEL_CONFIG_GROUP}.options.noFuelAccessorials`, noFuelAccessorialOptions),
    P.$set(`${GC.TEL_CONFIG_GROUP}.options.accessorialConfigList`, accessorialConfigList),
    state,
  );
};

const getAccessorialConfigListForCloConfigSuccess = (state, data) => {
  const mapper = R.map(({ displayedValue, originalConfigGuid }) => ({
    [GC.FIELD_LABEL]: displayedValue,
    [GC.FIELD_VALUE]: originalConfigGuid,
  }));
  const accessorials = mapper(data);
  const orderAccessorials = R.compose(
    mapper,
    R.filter(({ fuelRelated, internalExpense }) => R.and(
      G.isFalse(fuelRelated),
      R.or(G.isFalse(internalExpense), G.isNilOrEmpty(internalExpense)),
    )),
  )(data);
  const accessorialConfigList = R.compose(
    R.indexBy(R.prop(GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID)),
    R.map((item) => R.compose(
      R.assoc(GC.FIELD_ACCESSORIAL_ACCESSORIAL_GUID, R.prop(GC.FIELD_ORIGINAL_CONFIG_GUID, item)),
      R.pick([
        GC.FIELD_NON_TAXABLE,
        GC.FIELD_FUEL_RELATED,
        GC.FIELD_DISPLAYED_VALUE,
        GC.FIELD_CHARGE_RATE_TYPE,
        GC.FIELD_CHARGE_RATE_UNIT,
        GC.FIELD_CHARGE_INTERNAL_EXPENSE,
      ]),
    )(item)),
  )(data);

  return P.$all(
    P.$set(`${GC.CLO_CONFIG_GROUP}.options.accessorials`, accessorials),
    P.$set(`${GC.CLO_CONFIG_GROUP}.options.orderAccessorials`, orderAccessorials),
    P.$set(`${GC.CLO_CONFIG_GROUP}.options.accessorialConfigList`, accessorialConfigList),
    state,
  );
};

const getSharedAccessorialListSuccess = (state, data) => {
  const accessorialConfigList = P.$get(`${GC.CLO_CONFIG_GROUP}.options.accessorialConfigList`, state);
  const mapper = R.map(({ displayedValue, assessorialGuid }) => ({
    [GC.FIELD_LABEL]: displayedValue,
    [GC.FIELD_VALUE]: assessorialGuid,
  }));
  const fuelAccessorials = R.compose(
    mapper,
    R.filter(R.propEq(true, 'fuelRelated')),
    R.values,
  )(accessorialConfigList);
  const sharedMapper = R.map(({ guid, name }) => ({
    [GC.FIELD_VALUE]: guid,
    [GC.FIELD_LABEL]: `${name} (${G.getWindowLocale('titles:shared', 'Shared')})`,
  }));
  const shared = sharedMapper(data);
  const fuelAccessorialOptions = R.concat(shared, fuelAccessorials);

  return P.$all(
    P.$set('sharedAccessorialList', data),
    P.$set(`${GC.CLO_CONFIG_GROUP}.options.fuelAccessorialOptions`, fuelAccessorialOptions),
    state,
  );
};

const getDataSourceListSuccess = (state, data) => {
  const dataSourceList = R.map(({ dataSource }) => dataSource, data);
  const indexedDataSourceList = R.indexBy(
    R.prop(GC.FIELD_CONFIGURATION_COMMUNICATION_DOCUMENT_TEMPLATE_DATA_SOURCE),
    data,
  );
  return P.$all(
    P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.dataSourceList`, dataSourceList),
    P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.indexedDataSourceList`, indexedDataSourceList),
    state,
  );
};

const getIntegrationConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) {
    return P.$all(
      P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.integrationConfigList`, []),
      P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.integrationConfigs`, []),
      state,
    );
  }
  const integrationConfigList = R.indexBy(R.prop(GC.FIELD_GUID), data);
  const integrationConfigOptions = R.compose(
    R.map(({ name, guid }) => ({ [GC.FIELD_LABEL]: name, [GC.FIELD_VALUE]: guid })),
  )(data);
  return P.$all(
    P.$set(
      `${GC.INTEGRATION_CONFIG_GROUP}.integrationConfigList`,
      integrationConfigList,
    ),
    P.$set(
      `${GC.INTEGRATION_CONFIG_GROUP}.options.integrationConfigs`,
      integrationConfigOptions,
    ),
    state,
  );
};

const receivedIntegrationConfigSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.integrationConfigList.${data.guid}`, data, state)
);

const getLoadBoardIntegrationConfigListSuccess = (state, data) => {
  const elements = R.prop('elements', data);

  if (G.isNilOrEmpty(elements)) return state;

  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      {
        index,
        editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        deletable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
      },
    )),
  )(elements);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.loadBoardConfigList`, list, state);
};

const createOrUpdateLoadBoardIntegrationConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const { guid } = data;
  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'loadBoardConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.loadBoardConfigList`, newList, state);
};

const removeLoadBoardIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.loadBoardConfigList.${guid}`, state)
);

const updateLoadBoardIntegrationConfigState = (state, data) => {
  const lbConfigs = R.pathOr([], [GC.INTEGRATION_CONFIG_GROUP, 'loadBoardConfigList'], state);

  if (R.isEmpty(lbConfigs)) return state;

  const guid = G.getGuidFromObject(data);
  const loadBoardToUpdate = R.prop(guid, lbConfigs);

  let index;

  if (R.isNil(loadBoardToUpdate)) {
    index = R.length(R.values(lbConfigs));
  } else {
    index = R.prop('index', loadBoardToUpdate);
  }

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.loadBoardConfigList.${guid}`,
    {...data, index},
    state,
  );
};

const getTollChargesConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.tollChargeList`, list, state);
};

const createOrUpdateTollChargesConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const { guid } = data;
  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'tollChargeList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.tollChargeList`, newList, state);
};

const removeTollChargesConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.tollChargeList.${guid}`, state)
);

const getExpenseTypeListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.expenseTypeList`, list, state);
};

const createOrUpdateExpenseTypeSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const { guid } = data;

  const list = R.path([GC.INVOICE_CONFIG_GROUP, 'expenseTypeList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INVOICE_CONFIG_GROUP}.expenseTypeList`, newList, state);
};

const removeExpenseTypeSuccess = (state, guid) => (
  P.$drop(`${GC.INVOICE_CONFIG_GROUP}.expenseTypeList.${guid}`, state)
);

const getDistanceCalculatorConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) {
    return P.$all(
      P.$set(`${GC.GENERAL_CONFIG_GROUP}.distanceCalculatorConfigList`, []),
      P.$set(`${GC.GENERAL_CONFIG_GROUP}.options.distanceCalculatorConfigOptions`, []),
      state,
    );
  }
  const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();
  const mappedData = R.map(
    (item) => R.assoc(
      'editable',
      R.propEq(currentBranchGuid, GC.BRANCH_GUID, item),
      item,
    ),
    data,
  );
  const distanceCalculatorConfigOptions = G.mapNameGuidObjectPropsToLabelValueObject(mappedData);

  return P.$all(
    P.$set(
      `${GC.GENERAL_CONFIG_GROUP}.distanceCalculatorConfigList`,
      mappedData,
    ),
    P.$set(
      `${GC.GENERAL_CONFIG_GROUP}.options.distanceCalculatorConfigOptions`,
      distanceCalculatorConfigOptions,
    ),
    state,
  );
};

const getCrossDockLocationsSuccess = (state, data) => (
  P.$set(
    `${GC.TEL_CONFIG_GROUP}.options.crossDockLocations`,
    R.map((location) => ({
      value: R.prop(GC.FIELD_TEMPLATE_ID, location),
      label: R.prop(GC.FIELD_TEMPLATE_ID, location),
    }), data),
    state,
  )
);

const getCarrierListSuccess = (state, data) => (
  P.$set(`${GC.TEL_CONFIG_GROUP}.options.carriers`, G.mapNameGuidToLabelValue(data), state)
);

const receivedCarrierOnboardingListSuccess = (state, data) => (
  P.$set(`${GC.CARRIER_CONFIG_GROUP}.onboardingPackageList`, data, state)
);

const deleteCarrierOnboardingPackageSuccess = (state) => (
  P.$all(
    P.$set(`${GC.CARRIER_CONFIG_GROUP}.onboardingPackageList`, []),
    P.$set(`${GC.CARRIER_CONFIG_GROUP}.onboardingPackageDocumentList`, {}),
    state,
  )
);

const createOrUpdateCarrierOnboardingPackageSuccess = (state, data) => (
  P.$set(`${GC.CARRIER_CONFIG_GROUP}.onboardingPackageList`, R.of(Array, data), state)
);

const receivedCarrierOnboardingDocumentListSuccess = (state, data) => (
  P.$set(`${GC.CARRIER_CONFIG_GROUP}.onboardingPackageDocumentList`, R.indexBy(R.prop(GC.FIELD_GUID), data), state)
);

const deleteCarrierOnboardingPackageDocumentSuccess = (state, data) => (
  P.$drop(`${GC.CARRIER_CONFIG_GROUP}.onboardingPackageDocumentList.${data}`, state)
);

const createOrUpdateCarrierOnboardingPackageDocumentSuccess = (state, data) => (
  P.$set(
    `${GC.CARRIER_CONFIG_GROUP}.onboardingPackageDocumentList`,
    R.assoc(
      R.prop(GC.FIELD_GUID, data),
      data,
      R.path([GC.CARRIER_CONFIG_GROUP, 'onboardingPackageDocumentList'], state),
    ),
    state,
  )
);

const getTermsAndConditionsDocumentSuccess = (state, data) => {
  if (R.or(G.isString(data), G.isNilOrEmpty(data))) return P.$set(`${data}.termsAndConditionsDocument`, [], state);

  const configGroup = G.getPropFromObject('configGroup', data);

  const list = R.of(Array,
    R.assoc('editable', R.pathEq(G.getAmousCurrentBranchGuidFromWindow(), [GC.BRANCH_GUID], data), data),
  );

  return P.$set(`${configGroup}.termsAndConditionsDocument`, list, state);
};

const getCloDispatchBoardActionListSuccess = (state, data) => (
  P.$set(`${GC.UI_CONFIG_GROUP}.cloDispatchBoardActionPanel`, data, state)
);

const getTelDispatchBoardActionListSuccess = (state, data) => (
  P.$set(`${GC.UI_CONFIG_GROUP}.telDispatchBoardActionPanel`, data, state)
);

const getCentralizedDispatchActionListSuccess = (state, data) => (
  P.$set(`${GC.UI_CONFIG_GROUP}.centralizedDispatchActionPanel`, data, state)
);

const getGeofencingZoneColorConfigSuccess = (state, data) => (
  P.$set(`${GC.UI_CONFIG_GROUP}.geofencingZoneColorConfig`, data, state)
);

const setAvailableLocationTypes = (state, data) => (
  P.$set(`${GC.TEMPLATES_CONFIG_GROUP}.options.availableLocationTypes`, data, state)
);

// gps integration config
const getGpsIntegrationConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.gpsIntegrationList`, list, state);
};

const createOrUpdateGpsIntegrationConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;
  const { guid } = data;
  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'gpsIntegrationList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.gpsIntegrationList`, newList, state);
};

const removeGpsIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.gpsIntegrationList.${guid}`, state)
);

// dispatch integration config
const getDispatchIntegrationConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.dispatchIntegrationList`, list, state);
};

const createOrUpdateDispatchIntegrationConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'dispatchIntegrationList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.dispatchIntegrationList`, newList, state);
};

const removeDispatchIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.dispatchIntegrationList.${guid}`, state)
);

// status code mapping
const getStatusCodeMappingListSuccess = (state, { data, listName }) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.${listName}`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateStatusCodeMappingSuccess = (state, { data, listName }) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, listName], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.${listName}`, newList, state);
};

const removeStatusCodeMappingSuccess = (state, { guid, listName }) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.${listName}.${guid}`, state)
);

// status reason code mapping
const getStatusReasonCodeMappingListSuccess = (state, { data, listName }) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.${listName}`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateStatusReasonCodeMappingSuccess = (state, { data, listName }) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, listName], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.${listName}`, newList, state);
};

const removeStatusReasonCodeMappingSuccess = (state, { guid, listName }) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.${listName}.${guid}`, state)
);

// service type mapping
const getServiceTypeMappingListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.serviceTypeMappingList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateServiceTypeMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'serviceTypeMappingList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.serviceTypeMappingList`, newList, state);
};

const removeServiceTypeMappingSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.serviceTypeMappingList.${guid}`, state)
);

// default order accessorials
const getDefaultOrderAccessorialListSuccess = (state, data) => (
  P.$set(
    `${GC.CLO_CONFIG_GROUP}.defaultOrderAccessorialList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateDefaultOrderAccessorialSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.CLO_CONFIG_GROUP, 'defaultOrderAccessorialList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  G.setItemToWindow('amousDefaultOrderAccessorials', {});

  return P.$set(`${GC.CLO_CONFIG_GROUP}.defaultOrderAccessorialList`, newList, state);
};

const removeDefaultOrderAccessorialSuccess = (state, guid) => {
  G.setItemToWindow('amousDefaultOrderAccessorials', {});

  return P.$drop(`${GC.CLO_CONFIG_GROUP}.defaultOrderAccessorialList.${guid}`, state);
};


// default carrier accessorials
const getDefaultCarrierAccessorialListSuccess = (state, data) => (
  P.$set(
    `${GC.TEL_CONFIG_GROUP}.defaultCarrierAccessorialList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateDefaultCarrierAccessorialSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.TEL_CONFIG_GROUP, 'defaultCarrierAccessorialList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);
  G.setItemToWindow('amousDefaultCarrierAccessorials', {});

  return P.$set(`${GC.TEL_CONFIG_GROUP}.defaultCarrierAccessorialList`, newList, state);
};

const removeDefaultCarrierAccessorialSuccess = (state, guid) => {
  G.setItemToWindow('amousDefaultCarrierAccessorials', {});

  return P.$drop(`${GC.TEL_CONFIG_GROUP}.defaultCarrierAccessorialList.${guid}`, state);
};

// carrier synchronization
const getCarrierSynchronizationConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const editable = R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, data);

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.carrierSynchronization`,
    R.of(Array, R.assoc('editable', editable, data)),
    state,
  );
};

const removeCarrierSynchronizationConfigSuccess = (state) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.carrierSynchronization`, [], state)
);

// carrier integration override
const getCarrierIntegrationOverrideListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.carrierIntegrationOverrideList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateCarrierIntegrationOverrideSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'carrierIntegrationOverrideList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.carrierIntegrationOverrideList`, newList, state);
};

const removeCarrierIntegrationOverrideSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.carrierIntegrationOverrideList.${guid}`, state)
);

// integration mail config
const getIntegrationMailConfigSuccess = (state, data) => (
  P.$set(
    `${GC.COMMUNICATION_CONFIG_GROUP}.integrationMailConfig`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight({ index, deletable: item.editable }, item)),
    )(R.or(data, [])),
    state,
  )
);

// mail template config
const getMailTemplateConfigSuccess = (state, data) => (
  P.$set(
    `${GC.COMMUNICATION_CONFIG_GROUP}.mailTemplateConfig`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight({ index, deletable: item.editable }, item)),
    )(R.or(data, [])),
    state,
  )
);
const getMailTemplateTypesSuccess = (state, data) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.mailTemplateTypes`, data, state)
);

const getMailTemplateNamesByTypeSuccess = (state, data) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.mailTemplateNames`, data, state)
);

const getMailTemplateByKeySuccess = (state, data) => (
  P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.mailTemplateByKey`, data, state)
);

// document type mapping
const getDocumentTypeMappingListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.documentTypeMappingList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateDocumentTypeMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'documentTypeMappingList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.documentTypeMappingList`, newList, state);
};

const removeDocumentTypeMappingSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.documentTypeMappingList.${guid}`, state)
);

// outbound document type mapping
const getOutboundDocumentTypeMappingListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.outboundDocumentTypeMappingList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateOutboundDocumentTypeMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'outboundDocumentTypeMappingList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.outboundDocumentTypeMappingList`, newList, state);
};

const removeOutboundDocumentTypeMappingSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.outboundDocumentTypeMappingList.${guid}`, state)
);

// document hub config
const getDocumentHubConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.documentHubConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateDocumentHubConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'documentHubConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.documentHubConfigList`, newList, state);
};

const removeDocumentHubConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.documentHubConfigList.${guid}`, state)
);

// accounting
const getAccountingConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.accountingConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateAccountingConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'accountingConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.accountingConfigList`, newList, state);
};

const removeAccountingConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.accountingConfigList.${guid}`, state)
);

// carrier pickup requester
const getCarrierPickupRequesterListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.carrierPickupRequesterList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
          deletable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateCarrierPickupRequesterSuccess = (state, data) => {
  const { guid, name } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'carrierPickupRequesterList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$all(
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.carrierPickupRequesterList`, newList),
    P.$set(
      `${GC.INTEGRATION_CONFIG_GROUP}.options.availableCarrierPickupRequesterList.${guid}`,
      { guid, name },
    ),
    state,
  );
};

const removeCarrierPickupRequesterSuccess = (state, guid) => (
  P.$all(
    P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.carrierPickupRequesterList.${guid}`),
    P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.options.availableCarrierPickupRequesterList.${guid}`),
    state,
  )
);

const getAvailableCarrierPickupRequesterListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.options.availableCarrierPickupRequesterList`,
    R.indexBy(R.prop(GC.FIELD_GUID), data),
    state,
  )
);

// carrier edi loader config
const getCarrierEdiLoaderConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.carrierEdiLoaderConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateCarrierEdiLoaderConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'carrierEdiLoaderConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.carrierEdiLoaderConfigList`, newList, state);
};

const removeCarrierEdiLoaderConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.carrierEdiLoaderConfigList.${guid}`, state)
);

const getCarrierOptionsSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.options.carriers`,
    G.addEmptyOptionToDropDown(R.map(({ guid, name }) => ({
      [GC.FIELD_VALUE]: guid,
      [GC.FIELD_LABEL]: name,
    }), R.or(data, []))),
    state,
  )
);

// carrier edi exporter config
const getCarrierEdiExporterConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.carrierEdiExporterConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateCarrierEdiExporterConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'carrierEdiExporterConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.carrierEdiExporterConfigList`, newList, state);
};

const removeCarrierEdiExporterConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.carrierEdiExporterConfigList.${guid}`, state)
);

// transportation mode config mapping
const getTransportationModeConfigMappingListSuccess = (state, { data, configGroup }) => (
  P.$set(
    `${configGroup}.transportationModeConfigMappingList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateTransportationModeConfigMappingSuccess = (state, data) => {
  const { guid, configGroup } = data;

  const list = R.path([configGroup, 'transportationModeConfigMappingList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${configGroup}.transportationModeConfigMappingList`, newList, state);
};

const removeTransportationModeConfigMappingSuccess = (state, { guid, configGroup }) => (
  P.$drop(`${configGroup}.transportationModeConfigMappingList.${guid}`, state)
);

// vin lookup config
const getVinLookupConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.vinLookupConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(R.of(Array, data)),
    state,
  );
};

const createOrUpdateVinLookupConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'vinLookupConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.vinLookupConfigList`, newList, state);
};

const removeVinLookupConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.vinLookupConfigList.${guid}`, state)
);

// load custom status config
const getLoadCustomStatusListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(
    `${GC.GENERAL_CONFIG_GROUP}.loadCustomStatusConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(data),
    state,
  );
};

const createOrUpdateLoadCustomStatusSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.GENERAL_CONFIG_GROUP, 'loadCustomStatusConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.GENERAL_CONFIG_GROUP}.loadCustomStatusConfigList`, newList, state);
};

const removeLoadCustomStatusSuccess = (state, guid) => (
  P.$drop(`${GC.GENERAL_CONFIG_GROUP}.loadCustomStatusConfigList.${guid}`, state)
);

// factoring integration config
const getFactoringIntegrationConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.factoringIntegrationConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(data),
    state,
  );
};

const createOrUpdateFactoringIntegrationConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'factoringIntegrationConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.factoringIntegrationConfigList`, newList, state);
};

const removeFactoringIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.factoringIntegrationConfigList.${guid}`, state)
);

// equipment mapping config
const getEquipmentMappingConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.equipmentMappingConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(data),
    state,
  );
};

const createOrUpdateEquipmentMappingConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'equipmentMappingConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.equipmentMappingConfigList`, newList, state);
};

const removeEquipmentMappingConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.equipmentMappingConfigList.${guid}`, state)
);

// available edi export mappers
const getAvailableEdiExportMappersSuccess = (state, mappers) => {
  const getOptions = (availableMappers) => G.addEmptyOptionToDropDown(
    R.filter(({ value }) => R.includes(value, R.or(mappers, [])), availableMappers),
  );

  const optionsForExporter = getOptions(availableEdiExportMappers);
  const optionsForLoader = getOptions(R.concat(
    availableEdiExportMappers,
    availableEdiLoaderExportMappers,
  ));

  return P.$all(
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.availableEdiExportMappers`, optionsForExporter),
    P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.options.availableEdiExportMappersForLoader`, optionsForLoader),
    state,
  );
};

// margin violation rule
const getMarginViolationRuleListSuccess = (state, data) => (
  P.$set(
    `${GC.RATE_ENGINE_CONFIG_GROUP}.marginViolationRuleList`,
    R.map((entity) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, entity),
      entity,
    ), data),
    state,
  )
);

const removeMarginViolationRuleSuccess = (state, data) => {
  const list = R.pathOr([], [GC.RATE_ENGINE_CONFIG_GROUP, 'marginViolationRuleList'], state);
  const index = R.findIndex(R.propEq(data, GC.FIELD_GUID), list);

  return P.$drop(`${GC.RATE_ENGINE_CONFIG_GROUP}.marginViolationRuleList.${index}`, state);
};

const createOrUpdateMarginViolationRuleSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.RATE_ENGINE_CONFIG_GROUP, 'marginViolationRuleList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.RATE_ENGINE_CONFIG_GROUP}.marginViolationRuleList`, data, state);
  }

  return P.$set(`${GC.RATE_ENGINE_CONFIG_GROUP}.marginViolationRuleList.${index}`, data, state);
};

// onboarding integration config
const getOnboardingIntegrationConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.DRIVER_CONFIG_GROUP}.onboardingIntegrationList`,
    R.map((item) => R.assoc('deletable', item.editable, item), R.or(data, [])),
    state,
  )
);

// trailer tracking integration config
const getTrailerTrackingIntegrationConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.trailerTrackingIntegrationList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          deletable: item.editable,
        },
      )),
    )(data),
    state,
  );
};

const createOrUpdateTrailerTrackingIntegrationConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'trailerTrackingIntegrationList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.mergeRight(data, { index, deletable: data.editable });
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.trailerTrackingIntegrationList`, newList, state);
};

const removeTrailerTrackingIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.trailerTrackingIntegrationList.${guid}`, state)
);

// los subscription config
const getLosSubscriptionConfigSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.losSubscriptionConfig`, data, state)
);

const updateLosSubscriptionConfigSuccess = (state, data) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.losSubscriptionConfig`, data, state)
);

const unlinkLosAccountSuccess = (state) => (
  P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.initialValues.${GC.INTEGRATION_LOS_ACCOUNT_LINKED}`, false, state)
);

// los pricing plan
const getLosPricingPlanConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.losPricingPlanList`,
    G.indexByGuid(R.or(data, [])),
    state,
  )
);

const createOrUpdateLosPricingPlanConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'losPricingPlanList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.losPricingPlanList`, newList, state);
};

const removeLosPricingPlanConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.losPricingPlanList.${guid}`, state)
);

// carrier scorecard config
const getCarrierScorecardConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.CARRIER_CONFIG_GROUP}.carrierScorecardConfigList`,
    R.map((entity) => R.assoc(
      'editable',
      R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, entity),
      entity,
    ), data),
    state,
  )
);

const removeCarrierScorecardConfigSuccess = (state, data) => {
  const list = R.pathOr([], [GC.CARRIER_CONFIG_GROUP, 'carrierScorecardConfigList'], state);
  const index = R.findIndex(R.propEq(data, GC.FIELD_GUID), list);

  return P.$drop(`${GC.CARRIER_CONFIG_GROUP}.carrierScorecardConfigList.${index}`, state);
};

const createOrUpdateCarrierScoreCardconfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.CARRIER_CONFIG_GROUP, 'carrierScorecardConfigList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.CARRIER_CONFIG_GROUP}.carrierScorecardConfigList`, data, state);
  }

  return P.$set(`${GC.CARRIER_CONFIG_GROUP}.carrierScorecardConfigList.${index}`, data, state);
};

// terminal integration config
const getTerminalIntegrationConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.terminalIntegrationList`, list, state);
};

const createOrUpdateTerminalIntegrationConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'terminalIntegrationList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.terminalIntegrationList`, newList, state);
};

const removeTerminalIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.terminalIntegrationList.${guid}`, state)
);

// task type config
const getTaskTypeListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.TASK_MANAGEMENT_CONFIG_GROUP}.taskTypeList`, list, state);
};

const createOrUpdateTaskTypeSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.TASK_MANAGEMENT_CONFIG_GROUP, 'taskTypeList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.TASK_MANAGEMENT_CONFIG_GROUP}.taskTypeList`, newList, state);
};

const removeTaskTypeSuccess = (state, guid) => (
  P.$drop(`${GC.TASK_MANAGEMENT_CONFIG_GROUP}.taskTypeList.${guid}`, state)
);

// task status config
const getTaskStatusListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.TASK_MANAGEMENT_CONFIG_GROUP}.taskStatusList`, list, state);
};

const createOrUpdateTaskStatusSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.TASK_MANAGEMENT_CONFIG_GROUP, 'taskStatusList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.TASK_MANAGEMENT_CONFIG_GROUP}.taskStatusList`, newList, state);
};

const removeTaskStatusSuccess = (state, guid) => (
  P.$drop(`${GC.TASK_MANAGEMENT_CONFIG_GROUP}.taskStatusList.${guid}`, state)
);

// document sending config
const getDocumentSendingConfigListSuccess = (state, data) => (
  P.$set(
    `${GC.COMMUNICATION_CONFIG_GROUP}.documentSendingList`,
    R.map(
      (item) => R.assoc(
        'editable',
        R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        item,
      ),
      R.or(data, []),
    ),
    state,
  )
);

const createOrUpdateDocumentSendingConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.pathOr([], [GC.COMMUNICATION_CONFIG_GROUP, 'documentSendingList'], state);
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), list);

  if (R.lt(index, 0)) {
    return P.$add(`${GC.COMMUNICATION_CONFIG_GROUP}.documentSendingList`, data, state);
  }

  return P.$set(`${GC.COMMUNICATION_CONFIG_GROUP}.documentSendingList.${index}`, data, state);
};

const removeDocumentSendingConfigSuccess = (state, guid) => {
  const index = R.findIndex(
    R.propEq(guid, GC.FIELD_GUID),
    R.path([GC.COMMUNICATION_CONFIG_GROUP, 'documentSendingList'], state),
  );

  return P.$drop(`${GC.COMMUNICATION_CONFIG_GROUP}.documentSendingList.${index}`, state);
};

// transportation mode grouping
const getTransportationModeGroupingListSuccess = (state, data) => (
  P.$set(
    `${GC.GENERAL_CONFIG_GROUP}.transportationModeGroupingList`,
    R.map((item) =>
      R.assoc('editable', R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item), item),
      data,
    ),
    state,
  )
);

const removeTransportationModeGroupingSuccess = (state, data) => {
  const index = R.findIndex(
    R.propEq(data, GC.FIELD_GUID),
    R.pathOr([], [GC.GENERAL_CONFIG_GROUP, 'transportationModeGroupingList'], state),
  );

  return P.$drop(`${GC.GENERAL_CONFIG_GROUP}.transportationModeGroupingList.${index}`, state);
};

const createOrUpdateTransportationModeGroupingSuccess = (state, data) => {
  const { guid } = data;

  const index = R.findIndex(
    R.propEq(guid, GC.FIELD_GUID),
    R.pathOr([], [GC.GENERAL_CONFIG_GROUP, 'transportationModeGroupingList'], state),
  );

  if (R.lt(index, 0)) {
    return P.$add(`${GC.GENERAL_CONFIG_GROUP}.transportationModeGroupingList`, data, state);
  }

  return P.$set(`${GC.GENERAL_CONFIG_GROUP}.transportationModeGroupingList.${index}`, data, state);
};

// cross border integration config
const getCrossBorderIntegrationConfigSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const list = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(data);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.crossBorderIntegrationConfigList`, list, state);
};

const createOrUpdateCrossBorderIntegrationConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'crossBorderIntegrationConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.mergeRight(data, { index, deletable: data.editable });
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.crossBorderIntegrationConfigList`, newList, state);
};

const removeCrossBorderIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.crossBorderIntegrationConfigList.${guid}`, state)
);

// truck type mapping
const getTruckTypeMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.truckTypeMappingList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(data),
    state,
  );
};

const createOrUpdateTruckTypeMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'truckTypeMappingList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.truckTypeMappingList`, newList, state);
};

const removeTruckTypeMappingSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.truckTypeMappingList.${guid}`, state)
);

// trailer type mapping
const getTrailerTypeMappingListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  return P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.trailerTypeMappingList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(data),
    state,
  );
};

const createOrUpdateTrailerTypeMappingSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'trailerTypeMappingList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.trailerTypeMappingList`, newList, state);
};

const removeTrailerTypeMappingSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.trailerTypeMappingList.${guid}`, state)
);

// equipment service auto create
const getEquipmentServiceAutoCreateConfigListSuccess = (state, { data, configGroup }) => (
  P.$set(
    `${configGroup}.equipmentServiceAutoCreateConfigList`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        {
          index,
          editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item),
        },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateEquipmentServiceAutoCreateConfigSuccess = (state, data) => {
  const { guid, configGroup } = data;

  const list = R.path([configGroup, 'equipmentServiceAutoCreateConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${configGroup}.equipmentServiceAutoCreateConfigList`, newList, state);
};

const removeEquipmentServiceAutoCreateConfigSuccess = (state, { guid, configGroup }) => (
  P.$drop(`${configGroup}.equipmentServiceAutoCreateConfigList.${guid}`, state)
);

const setServiceVendorOptions = (state, data) => (
  P.$set(`${GC.SERVICE_VENDOR_CONFIG_GROUP}.options.serviceVendors`, data, state)
);

const setServiceTypeOptions = (state, data) => (
  P.$set(`${GC.FLEET_GENERAL_CONFIG_GROUP}.options.serviceTypes`, data, state)
);

// reference type mapping
const getReferenceTypeMappingListSuccess = (state, { data, listName }) => (
  P.$set(
    `${GC.INTEGRATION_CONFIG_GROUP}.${listName}`,
    R.compose(
      R.indexBy(R.prop(GC.FIELD_GUID)),
      G.mapIndexed((item, index) => R.mergeRight(
        item,
        { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
      )),
    )(R.or(data, [])),
    state,
  )
);

const createOrUpdateReferenceTypeMappingSuccess = (state, { data, listName }) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, listName], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.${listName}`, newList, state);
};

const removeReferenceTypeMappingSuccess = (state, { guid, listName }) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.${listName}.${guid}`, state)
);

// repair shop integration config
const getRepairShopIntegrationConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.repairShopIntegrationConfigList`, mapped, state);
};

const createOrUpdateRepairShopIntegrationConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'repairShopIntegrationConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.repairShopIntegrationConfigList`, newList, state);
};

const removeRepairShopIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.repairShopIntegrationConfigList.${guid}`, state)
);

// eta calculator
const getEtaCalculatorConfigListSuccess = (state, data) => {
  if (G.isNilOrEmpty(data)) return state;

  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.GENERAL_CONFIG_GROUP}.etaCalculatorConfigList`, mapped, state);
};

const createOrUpdateEtaCalculatorConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.GENERAL_CONFIG_GROUP, 'etaCalculatorConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.GENERAL_CONFIG_GROUP}.etaCalculatorConfigList`, newList, state);
};

const removeEtaCalculatorConfigSuccess = (state, guid) => (
  P.$drop(`${GC.GENERAL_CONFIG_GROUP}.etaCalculatorConfigList.${guid}`, state)
);

// accessorial mapping config
const getAccessorialMappingListSuccess = (state, { data, mappingType = 'outbound' }) => {
  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.${mappingType}AccessorialMappingList`, mapped, state);
};

const removeAccessorialMappingSuccess = (state, { guid, mappingType = 'outbound' }) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.${mappingType}AccessorialMappingList.${guid}`, state)
);

const createOrUpdateAccessorialMappingSuccess = (state, { data, mappingType = 'outbound' }) => {
  const { guid } = data;

  const listName = `${mappingType}AccessorialMappingList`;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, listName], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.${listName}`, newList, state);
};

// container tracking config
const getContainerTrackingConfigListSuccess = (state, data) => {
  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.containerTrackingConfigList`, mapped, state);
};

const createOrUpdateContainerTrackingConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'containerTrackingConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.containerTrackingConfigList`, newList, state);
};

const removeContainerTrackingConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.containerTrackingConfigList.${guid}`, state)
);

// crm integration config
const getCRMIntegrationConfigListSuccess = (state, data) => {
  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.crmIntegrationConfigList`, mapped, state);
};

const createOrUpdateCRMIntegrationConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'crmIntegrationConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.crmIntegrationConfigList`, newList, state);
};

const removeCRMIntegrationConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.crmIntegrationConfigList.${guid}`, state)
);

// service mapping
const getServiceMappingListByTypeSuccess = (state, { data, mappingType }) => {
  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.${mappingType}ServiceConfigList`, mapped, state);
};

const createOrUpdateServiceMappingByTypeSuccess = (state, { data, mappingType }) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, `${mappingType}ServiceConfigList`], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.${mappingType}ServiceConfigList`, newList, state);
};

const removeServiceMappingByTypeSuccess = (state, { guid, mappingType }) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.${mappingType}ServiceConfigList.${guid}`, state)
);

// order type mapping
const getOrderTypeMappingListByTypeSuccess = (state, { data, mappingType }) => {
  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.CLO_CONFIG_GROUP}.${mappingType}OrderTypeConfigList`, mapped, state);
};

const createOrUpdateOrderTypeMappingByTypeSuccess = (state, { data, mappingType }) => {
  const { guid } = data;

  const list = R.path([GC.CLO_CONFIG_GROUP, `${mappingType}OrderTypeConfigList`], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.CLO_CONFIG_GROUP}.${mappingType}OrderTypeConfigList`, newList, state);
};

const removeOrderTypeMappingByTypeSuccess = (state, { guid, mappingType }) => (
  P.$drop(`${GC.CLO_CONFIG_GROUP}.${mappingType}OrderTypeConfigList.${guid}`, state)
);

// freight safety config
const getFreightSafetyConfigListSuccess = (state, data) => {
  const mapped = R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    G.mapIndexed((item, index) => R.mergeRight(
      item,
      { index, editable: R.propEq(G.getAmousCurrentBranchGuidFromWindow(), GC.BRANCH_GUID, item) },
    )),
  )(R.or(data, []));

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.freightSafetyConfigList`, mapped, state);
};

const createOrUpdateFreightSafetyConfigSuccess = (state, data) => {
  const { guid } = data;

  const list = R.path([GC.INTEGRATION_CONFIG_GROUP, 'freightSafetyConfigList'], state);
  const index = R.pathOr(R.length(R.values(list)), [guid, 'index'], list);
  const newListItem = R.assoc('index', index, data);
  const newList = R.assoc(guid, newListItem, list);

  return P.$set(`${GC.INTEGRATION_CONFIG_GROUP}.freightSafetyConfigList`, newList, state);
};

const removeFreightSafetyConfigSuccess = (state, guid) => (
  P.$drop(`${GC.INTEGRATION_CONFIG_GROUP}.freightSafetyConfigList.${guid}`, state)
);

const cleanConfigState = () => initialState;

export default createReducer({
  //
  [socketLBConfigStateReceived]: updateLoadBoardIntegrationConfigState,
  //
  [A.cleanConfigState]: cleanConfigState,
  [A.setConfigWarning]: setConfigWarning,
  [A.setDocumentsFilter]: setDocumentsFilter,
  [A.setConfigTabsVisited]: setConfigTabsVisited,
  [A.getCarrierListSuccess]: getCarrierListSuccess,
  [A.createDocumentSuccess]: createDocumentSuccess,
  [A.updateDocumentSuccess]: updateDocumentSuccess,
  [A.getFuelCardListSuccess]: getFuelCardListSuccess,
  [A.receivedFuelCardSuccess]: receivedFuelCardSuccess,
  [A.getConfigByGroupSuccess]: getConfigByGroupSuccess,
  [A.setServiceMappingFilter]: setServiceMappingFilter,
  [A.getEdiConfigsListSuccess]: getEdiConfigsListSuccess,
  [A.getDataSourceListSuccess]: getDataSourceListSuccess,
  [A.createAccessorialSuccess]: createAccessorialSuccess,
  [A.updateAccessorialSuccess]: updateAccessorialSuccess,
  [A.deleteAccessorialSuccess]: deleteAccessorialSuccess,
  [A.getStatusCodeListSuccess]: getStatusCodeListSuccess,
  [A.setAvailableLocationTypes]: setAvailableLocationTypes,
  [A.receivedEdiConfigsSuccess]: receivedEdiConfigsSuccess,
  [A.createNotificationSuccess]: createNotificationSuccess,
  [A.deleteNotificationSuccess]: deleteNotificationSuccess,
  [A.connectToFinancialSuccess]: connectToFinancialSuccess,
  [A.createStatusMessageSuccess]: createStatusMessageSuccess,
  [A.updateStatusMessageSuccess]: updateStatusMessageSuccess,
  [A.deleteStatusMessageSuccess]: deleteStatusMessageSuccess,
  [A.setActiveConfigGroupWithTab]: setActiveConfigGroupWithTab,
  [A.deleteDocumentConfigSuccess]: deleteDocumentConfigSuccess,
  [A.removeAdvancePaymentSuccess]: removeAdvancePaymentSuccess,
  [A.removeServiceMappingSuccess]: removeServiceMappingSuccess,
  [A.getCustomerBranchListSuccess]: getCustomerBranchListSuccess,
  [A.getDocumentsForConfigSuccess]: getDocumentsForConfigSuccess,
  [A.getCrossDockLocationsSuccess]: getCrossDockLocationsSuccess,
  [A.getServiceMappingListSuccess]: getServiceMappingListSuccess,
  [A.getAdvancePaymentListSuccess]: getAdvancePaymentListSuccess,
  [A.setNotificationTriggersFilter]: setNotificationTriggersFilter,
  [A.getGroupConfigsByNamesSuccess]: getGroupConfigsByNamesSuccess,
  [A.removeTollChargesConfigSuccess]: removeTollChargesConfigSuccess,
  [A.getNotificationTriggersSuccess]: getNotificationTriggersSuccess,
  [A.getSequencesGuidNameMapSuccess]: getSequencesGuidNameMapSuccess,
  [A.getAccessorialForConfigSuccess]: getAccessorialForConfigSuccess,
  [A.setNotificationObjectTypeFilter]: setNotificationObjectTypeFilter,
  [A.getSharedAccessorialListSuccess]: getSharedAccessorialListSuccess,
  [A.createServiceMappingListSuccess]: createServiceMappingListSuccess,
  [A.getEdiExporterConfigListSuccess]: getEdiExporterConfigListSuccess,
  [A.getTollChargesConfigListSuccess]: getTollChargesConfigListSuccess,
  [A.getIntegrationConfigListSuccess]: getIntegrationConfigListSuccess,
  [A.getDriverPayrollStatusesSuccess]: getDriverPayrollStatusesSuccess,
  [A.receivedEdiExporterConfigSuccess]: receivedEdiExporterConfigSuccess,
  [A.getEdiExtractorConfigListSuccess]: getEdiExtractorConfigListSuccess,
  [A.receivedIntegrationConfigSuccess]: receivedIntegrationConfigSuccess,
  [A.getCarrierInvoiceStatusesSuccess]: getCarrierInvoiceStatusesSuccess,
  [A.updateNotificationConfigsSuccess]: updateNotificationConfigsSuccess,
  [A.createDriverPayrollStatusSuccess]: createDriverPayrollStatusSuccess,
  [A.updateDriverPayrollStatusSuccess]: updateDriverPayrollStatusSuccess,
  [A.deleteDriverPayrollStatusSuccess]: deleteDriverPayrollStatusSuccess,
  [A.removeGpsIntegrationConfigSuccess]: removeGpsIntegrationConfigSuccess,
  [A.receivedEdiExtractorConfigSuccess]: receivedEdiExtractorConfigSuccess,
  [A.getCustomerInvoiceStatusesSuccess]: getCustomerInvoiceStatusesSuccess,
  [A.createCarrierInvoiceStatusSuccess]: createCarrierInvoiceStatusSuccess,
  [A.updateCarrierInvoiceStatusSuccess]: updateCarrierInvoiceStatusSuccess,
  [A.deleteCarrierInvoiceStatusSuccess]: deleteCarrierInvoiceStatusSuccess,
  [A.getStatusMessagesForConfigSuccess]: getStatusMessagesForConfigSuccess,
  [A.getGpsIntegrationConfigListSuccess]: getGpsIntegrationConfigListSuccess,
  [A.getSequencesByTypeForConfigSuccess]: getSequencesByTypeForConfigSuccess,
  [A.getNotificationListTriggersSuccess]: getNotificationListTriggersSuccess,
  [A.updateCustomerInvoiceStatusSuccess]: updateCustomerInvoiceStatusSuccess,
  [A.deleteCustomerInvoiceStatusSuccess]: deleteCustomerInvoiceStatusSuccess,
  [A.receivedBranchConfigsByNameSuccess]: receivedBranchConfigsByNameSuccess,
  [A.createOrUpdateServiceMappingSuccess]: createOrUpdateServiceMappingSuccess,
  [A.createOrUpdateAdvancePaymentSuccess]: createOrUpdateAdvancePaymentSuccess,
  [A.removeCarrierRateIntegrationSuccess]: removeCarrierRateIntegrationSuccess,
  [A.getGeofencingZoneColorConfigSuccess]: getGeofencingZoneColorConfigSuccess,
  [A.deleteCarrierDeactivationRuleSuccess]: deleteCarrierDeactivationRuleSuccess,
  [A.updateCarrierDeactivationRuleSuccess]: updateCarrierDeactivationRuleSuccess,
  [A.createCarrierDeactivationRuleSuccess]: createCarrierDeactivationRuleSuccess,
  [A.getTermsAndConditionsDocumentSuccess]: getTermsAndConditionsDocumentSuccess,
  [A.receivedCarrierOnboardingListSuccess]: receivedCarrierOnboardingListSuccess,
  [A.createCustomerInvoiceStatusesSuccess]: createCustomerInvoiceStatusesSuccess,
  [A.getCloDispatchBoardActionListSuccess]: getCloDispatchBoardActionListSuccess,
  [A.getParentBranchesByBranchGuidSuccess]: getParentBranchesByBranchGuidSuccess,
  [A.getTelDispatchBoardActionListSuccess]: getTelDispatchBoardActionListSuccess,
  [A.getCentralizedDispatchActionListSuccess]: getCentralizedDispatchActionListSuccess,
  [A.receivedAvailableDocumentTypesSuccess]: receivedAvailableDocumentTypesSuccess,
  [A.getReferenceTypesGuidsNamesMapSuccess]: getReferenceTypesGuidsNamesMapSuccess,
  [A.getFleetServiceInvoiceStatusesSuccess]: getFleetServiceInvoiceStatusesSuccess,
  [A.deleteCarrierOnboardingPackageSuccess]: deleteCarrierOnboardingPackageSuccess,
  [A.getCarrierDeactivationRuleListSuccess]: getCarrierDeactivationRuleListSuccess,
  [A.getAvailableInspectionTemplatesSuccess]: getAvailableInspectionTemplatesSuccess,
  [A.deleteFleetServiceInvoiceStatusSuccess]: deleteFleetServiceInvoiceStatusSuccess,
  [A.removeDispatchIntegrationConfigSuccess]: removeDispatchIntegrationConfigSuccess,
  [A.createOrUpdateTollChargesConfigSuccess]: createOrUpdateTollChargesConfigSuccess,
  [A.receivedAccessorialServiceCodesSuccess]: receivedAccessorialServiceCodesSuccess,
  [A.getDistanceCalculatorConfigListSuccess]: getDistanceCalculatorConfigListSuccess,
  [A.removeLoadBoardIntegrationConfigSuccess]: removeLoadBoardIntegrationConfigSuccess,
  [A.getDispatchIntegrationConfigListSuccess]: getDispatchIntegrationConfigListSuccess,
  [A.getReferenceTypesByScopeForConfigSuccess]: getReferenceTypesByScopeForConfigSuccess,
  [A.getLoadBoardIntegrationConfigListSuccess]: getLoadBoardIntegrationConfigListSuccess,
  [A.createOrUpdateGpsIntegrationConfigSuccess]: createOrUpdateGpsIntegrationConfigSuccess,
  [A.receivedCarrierRateIntegrationListSuccess]: receivedCarrierRateIntegrationListSuccess,
  [A.getAccessorialConfigListForTelConfigSuccess]: getAccessorialConfigListForTelConfigSuccess,
  [A.getAccessorialConfigListForCloConfigSuccess]: getAccessorialConfigListForCloConfigSuccess,
  [A.createOrUpdateCarrierRateIntegrationSuccess]: createOrUpdateCarrierRateIntegrationSuccess,
  [A.receivedCarrierOnboardingDocumentListSuccess]: receivedCarrierOnboardingDocumentListSuccess,
  [A.createOrUpdateCarrierOnboardingPackageSuccess]: createOrUpdateCarrierOnboardingPackageSuccess,
  [A.getBranchConfigsByNamesForCarrierGroupSuccess]: getBranchConfigsByNamesForCarrierGroupSuccess,
  [A.deleteCarrierOnboardingPackageDocumentSuccess]: deleteCarrierOnboardingPackageDocumentSuccess,
  [A.createOrUpdateDispatchIntegrationConfigSuccess]: createOrUpdateDispatchIntegrationConfigSuccess,
  [A.createOrUpdateFleetServiceInvoiceStatusSuccess]: createOrUpdateFleetServiceInvoiceStatusSuccess,
  [A.createOrUpdateLoadBoardIntegrationConfigSuccess]: createOrUpdateLoadBoardIntegrationConfigSuccess,
  [A.createOrUpdateCarrierOnboardingPackageDocumentSuccess]: createOrUpdateCarrierOnboardingPackageDocumentSuccess,
  // configs for
  [A.getConfigsForUiConfigSuccess]: handleGetConfigsForUiConfigSuccess,
  [getConfigsForCloConfigSuccess]: handleGetConfigsForCloConfigSuccess,
  [getBranchConfigsForRatingSuccess]: handleGetBranchConfigsForRatingSuccess,
  [getConfigsForInvoiceConfigSuccess]: handleGetConfigsForInvoiceConfigSuccess,
  [getGeneralConfigsForTelConfigSuccess]: handleGetGeneralConfigsForTelConfigSuccess,
  [A.getConfigsForCommunicationConfigSuccess]: getConfigsForCommunicationConfigSuccess,
  // status code mapping
  [A.removeStatusCodeMappingSuccess]: removeStatusCodeMappingSuccess,
  [A.getStatusCodeMappingListSuccess]: getStatusCodeMappingListSuccess,
  [A.createOrUpdateStatusCodeMappingSuccess]: createOrUpdateStatusCodeMappingSuccess,
  // status code mapping
  [A.removeStatusReasonCodeMappingSuccess]: removeStatusReasonCodeMappingSuccess,
  [A.getStatusReasonCodeMappingListSuccess]: getStatusReasonCodeMappingListSuccess,
  [A.createOrUpdateStatusReasonCodeMappingSuccess]: createOrUpdateStatusReasonCodeMappingSuccess,
  // qb account mapping
  [A.setQBAccountMappingFilter]: setQBAccountMappingFilter,
  [A.removeQBAccountMappingSuccess]: removeQBAccountMappingSuccess,
  [A.getQBAccountMappingListSuccess]: getQBAccountMappingListSuccess,
  [A.createOrUpdateQBAccountMappingSuccess]: createOrUpdateQBAccountMappingSuccess,
  // qb custom field mapping
  [A.removeQBCustomFieldMappingSuccess]: removeQBCustomFieldMappingSuccess,
  [A.getQBCustomFieldMappingListSuccess]: getQBCustomFieldMappingListSuccess,
  [A.createOrUpdateQBCustomFieldMappingSuccess]: createOrUpdateQBCustomFieldMappingSuccess,
  // qb tax code mapping
  [A.removeQBTaxCodeMappingSuccess]: removeQBTaxCodeMappingSuccess,
  [A.getQBTaxCodeMappingListSuccess]: getQBTaxCodeMappingListSuccess,
  [A.createOrUpdateQBTaxCodeMappingSuccess]: createOrUpdateQBTaxCodeMappingSuccess,
  // qb iif accessorial mapping
  [A.setQbIffAssessorialMappingFilter]: setQbIffAssessorialMappingFilter,
  [A.removeQBIIFAccessorialMappingSuccess]: removeQBIIFAccessorialMappingSuccess,
  [A.getQBIIFAccessorialMappingListSuccess]: getQBIIFAccessorialMappingListSuccess,
  [A.createOrUpdateQBIIFAccessorialMappingSuccess]: createOrUpdateQBIIFAccessorialMappingSuccess,
  // qb desktop connecting
  [A.removeQBDesktopConnectionSuccess]: removeQBDesktopConnectionSuccess,
  [A.getQBDesktopConnectionListSuccess]: getQBDesktopConnectionListSuccess,
  [A.createOrUpdateQBDesktopConnectionSuccess]: createOrUpdateQBDesktopConnectionSuccess,
  // propel hr assessorial mapping
  [A.removePropelHRAssessorialMappingSuccess]: removePropelHRAssessorialMappingSuccess,
  [A.getPropelHRAssessorialMappingListSuccess]: getPropelHRAssessorialMappingListSuccess,
  [A.createOrUpdatePropelHRAssessorialMappingSuccess]: createOrUpdatePropelHRAssessorialMappingSuccess,
  // paylocity code mapping
  [A.removePaylocityCodeMappingSuccess]: removePaylocityCodeMappingSuccess,
  [A.getPaylocityCodeMappingListSuccess]: getPaylocityCodeMappingListSuccess,
  [A.createOrUpdatePaylocityCodeMappingSuccess]: createOrUpdatePaylocityCodeMappingSuccess,
  // fuel card transaction mapping
  [A.removeFuelCardTransactionMappingSuccess]: removeFuelCardTransactionMappingSuccess,
  [A.getFuelCardTransactionMappingListSuccess]: getFuelCardTransactionMappingListSuccess,
  [A.createOrUpdateFuelCardTransactionMappingSuccess]: createOrUpdateFuelCardTransactionMappingSuccess,
  // sage account mapping
  [A.setSageAccountMappingFilter]: setSageAccountMappingFilter,
  [A.removeSageAccountMappingSuccess]: removeSageAccountMappingSuccess,
  [A.getSageAccountMappingListSuccess]: getSageAccountMappingListSuccess,
  [A.createOrUpdateSageAccountMappingSuccess]: createOrUpdateSageAccountMappingSuccess,
  // customer id mapping
  [A.removeCustomerIdMappingSuccess]: removeCustomerIdMappingSuccess,
  [A.getCustomerIdMappingListSuccess]: getCustomerIdMappingListSuccess,
  [A.createOrUpdateCustomerIdMappingSuccess]: createOrUpdateCustomerIdMappingSuccess,
  // department id mapping
  [A.removeDepartmentIdMappingSuccess]: removeDepartmentIdMappingSuccess,
  [A.getDepartmentIdMappingListSuccess]: getDepartmentIdMappingListSuccess,
  [A.createOrUpdateDepartmentIdMappingSuccess]: createOrUpdateDepartmentIdMappingSuccess,
  // public clo
  [A.getPublicCLOConfigSuccess]: getPublicCLOConfigSuccess,
  [A.removePublicCLOConfigSuccess]: removePublicCLOConfigSuccess,
  // service type mapping
  [A.removeServiceTypeMappingSuccess]: removeServiceTypeMappingSuccess,
  [A.getServiceTypeMappingListSuccess]: getServiceTypeMappingListSuccess,
  [A.createOrUpdateServiceTypeMappingSuccess]: createOrUpdateServiceTypeMappingSuccess,
  // carrier synchronization
  [A.getCarrierSynchronizationConfigSuccess]: getCarrierSynchronizationConfigSuccess,
  [A.removeCarrierSynchronizationConfigSuccess]: removeCarrierSynchronizationConfigSuccess,
  // default order accessorials
  [A.removeDefaultOrderAccessorialSuccess]: removeDefaultOrderAccessorialSuccess,
  [A.getDefaultOrderAccessorialListSuccess]: getDefaultOrderAccessorialListSuccess,
  [A.createOrUpdateDefaultOrderAccessorialSuccess]: createOrUpdateDefaultOrderAccessorialSuccess,
  // default carrier accessorials
  [A.removeDefaultCarrierAccessorialSuccess]: removeDefaultCarrierAccessorialSuccess,
  [A.getDefaultCarrierAccessorialListSuccess]: getDefaultCarrierAccessorialListSuccess,
  [A.createOrUpdateDefaultCarrierAccessorialSuccess]: createOrUpdateDefaultCarrierAccessorialSuccess,
  // user monitoring
  [A.removeUserMonitoringIntegrationSuccess]: removeUserMonitoringIntegrationSuccess,
  [A.receivedUserMonitoringIntegrationListSuccess]: receivedUserMonitoringIntegrationListSuccess,
  [A.createOrUpdateUserMonitoringIntegrationSuccess]: createOrUpdateUserMonitoringIntegrationSuccess,
  // carrier integration override
  [A.removeCarrierIntegrationOverrideSuccess]: removeCarrierIntegrationOverrideSuccess,
  [A.getCarrierIntegrationOverrideListSuccess]: getCarrierIntegrationOverrideListSuccess,
  [A.createOrUpdateCarrierIntegrationOverrideSuccess]: createOrUpdateCarrierIntegrationOverrideSuccess,
  // gl code mapping
  [A.setGLCodeMappingFilter]: setGLCodeMappingFilter,
  [A.removeGLCodeMappingSuccess]: removeGLCodeMappingSuccess,
  [A.getGLCodeMappingListSuccess]: getGLCodeMappingListSuccess,
  [A.createOrUpdateCLCodeMappingSuccess]: createOrUpdateCLCodeMappingSuccess,
  // integration mail config
  [A.getIntegrationMailConfigSuccess]: getIntegrationMailConfigSuccess,
  // mail template config
  [A.getMailTemplateTypesSuccess]: getMailTemplateTypesSuccess,
  [A.getMailTemplateByKeySuccess]: getMailTemplateByKeySuccess,
  [A.getMailTemplateConfigSuccess]: getMailTemplateConfigSuccess,
  [A.getMailTemplateNamesByTypeSuccess]: getMailTemplateNamesByTypeSuccess,
  // document type mapping
  [A.removeDocumentTypeMappingSuccess]: removeDocumentTypeMappingSuccess,
  [A.getDocumentTypeMappingListSuccess]: getDocumentTypeMappingListSuccess,
  [A.createOrUpdateDocumentTypeMappingSuccess]: createOrUpdateDocumentTypeMappingSuccess,
  // outbound document type mapping
  [A.removeOutboundDocumentTypeMappingSuccess]: removeOutboundDocumentTypeMappingSuccess,
  [A.getOutboundDocumentTypeMappingListSuccess]: getOutboundDocumentTypeMappingListSuccess,
  [A.createOrUpdateOutboundDocumentTypeMappingSuccess]: createOrUpdateOutboundDocumentTypeMappingSuccess,
  // document hub config
  [A.removeDocumentHubConfigSuccess]: removeDocumentHubConfigSuccess,
  [A.getDocumentHubConfigListSuccess]: getDocumentHubConfigListSuccess,
  [A.createOrUpdateDocumentHubConfigSuccess]: createOrUpdateDocumentHubConfigSuccess,
  // accounting
  [A.removeAccountingConfigSuccess]: removeAccountingConfigSuccess,
  [A.getAccountingConfigListSuccess]: getAccountingConfigListSuccess,
  [A.createOrUpdateAccountingConfigSuccess]: createOrUpdateAccountingConfigSuccess,
  // carrier pickup requester
  [A.removeCarrierPickupRequesterSuccess]: removeCarrierPickupRequesterSuccess,
  [A.getCarrierPickupRequesterListSuccess]: getCarrierPickupRequesterListSuccess,
  [A.createOrUpdateCarrierPickupRequesterSuccess]: createOrUpdateCarrierPickupRequesterSuccess,
  [A.getAvailableCarrierPickupRequesterListSuccess]: getAvailableCarrierPickupRequesterListSuccess,
  // invoice status rule
  [A.setInvoiceStatusRuleFilter]: setInvoiceStatusRuleFilter,
  [A.getInvoiceStatusRuleListSuccess]: getInvoiceStatusRuleListSuccess,
  // carrier edi loader config
  [A.getCarrierOptionsSuccess]: getCarrierOptionsSuccess,
  [A.removeCarrierEdiLoaderConfigSuccess]: removeCarrierEdiLoaderConfigSuccess,
  [A.getCarrierEdiLoaderConfigListSuccess]: getCarrierEdiLoaderConfigListSuccess,
  [A.createOrUpdateCarrierEdiLoaderConfigSuccess]: createOrUpdateCarrierEdiLoaderConfigSuccess,
  // carrier edi exporter config
  [A.removeCarrierEdiExporterConfigSuccess]: removeCarrierEdiExporterConfigSuccess,
  [A.getCarrierEdiExporterConfigListSuccess]: getCarrierEdiExporterConfigListSuccess,
  [A.createOrUpdateCarrierEdiExporterConfigSuccess]: createOrUpdateCarrierEdiExporterConfigSuccess,
  // transportation mode config mapping
  [A.removeTransportationModeConfigMappingSuccess]: removeTransportationModeConfigMappingSuccess,
  [A.getTransportationModeConfigMappingListSuccess]: getTransportationModeConfigMappingListSuccess,
  [A.createOrUpdateTransportationModeConfigMappingSuccess]: createOrUpdateTransportationModeConfigMappingSuccess,
  // vin lookup config
  [A.removeVinLookupConfigSuccess]: removeVinLookupConfigSuccess,
  [A.getVinLookupConfigListSuccess]: getVinLookupConfigListSuccess,
  [A.createOrUpdateVinLookupConfigSuccess]: createOrUpdateVinLookupConfigSuccess,
  // load custom status config
  [A.removeLoadCustomStatusSuccess]: removeLoadCustomStatusSuccess,
  [A.getLoadCustomStatusListSuccess]: getLoadCustomStatusListSuccess,
  [A.createOrUpdateLoadCustomStatusSuccess]: createOrUpdateLoadCustomStatusSuccess,
  // factoring integration config
  [A.removeFactoringIntegrationConfigSuccess]: removeFactoringIntegrationConfigSuccess,
  [A.getFactoringIntegrationConfigListSuccess]: getFactoringIntegrationConfigListSuccess,
  [A.createOrUpdateFactoringIntegrationConfigSuccess]: createOrUpdateFactoringIntegrationConfigSuccess,
  // equipment mapping config
  [A.removeEquipmentMappingConfigSuccess]: removeEquipmentMappingConfigSuccess,
  [A.getEquipmentMappingConfigListSuccess]: getEquipmentMappingConfigListSuccess,
  [A.createOrUpdateEquipmentMappingConfigSuccess]: createOrUpdateEquipmentMappingConfigSuccess,
  // available edi export mappers
  [A.getAvailableEdiExportMappersSuccess]: getAvailableEdiExportMappersSuccess,
  // margin violation rule
  [A.removeMarginViolationRuleSuccess]: removeMarginViolationRuleSuccess,
  [A.getMarginViolationRuleListSuccess]: getMarginViolationRuleListSuccess,
  [A.createOrUpdateMarginViolationRuleSuccess]: createOrUpdateMarginViolationRuleSuccess,
  // trailer tracking integration config
  [A.removeTrailerTrackingIntegrationConfigSuccess]: removeTrailerTrackingIntegrationConfigSuccess,
  [A.getTrailerTrackingIntegrationConfigListSuccess]: getTrailerTrackingIntegrationConfigListSuccess,
  [A.createOrUpdateTrailerTrackingIntegrationConfigSuccess]: createOrUpdateTrailerTrackingIntegrationConfigSuccess,
  // onboarding integration config
  [A.getOnboardingIntegrationConfigListSuccess]: getOnboardingIntegrationConfigListSuccess,
  // los subscription config
  [A.unlinkLosAccountSuccess]: unlinkLosAccountSuccess,
  [A.getLosSubscriptionConfigSuccess]: getLosSubscriptionConfigSuccess,
  [A.updateLosSubscriptionConfigSuccess]: updateLosSubscriptionConfigSuccess,
  // los pricing plan
  [A.removeLosPricingPlanConfigSuccess]: removeLosPricingPlanConfigSuccess,
  [A.getLosPricingPlanConfigListSuccess]: getLosPricingPlanConfigListSuccess,
  [A.createOrUpdateLosPricingPlanConfigSuccess]: createOrUpdateLosPricingPlanConfigSuccess,
  // carrier scorecard config
  [A.removeCarrierScorecardConfigSuccess]: removeCarrierScorecardConfigSuccess,
  [A.getCarrierScorecardConfigListSuccess]: getCarrierScorecardConfigListSuccess,
  [A.createOrUpdateCarrierScoreCardconfigSuccess]: createOrUpdateCarrierScoreCardconfigSuccess,
  // terminal integration config
  [A.removeTerminalIntegrationConfigSuccess]: removeTerminalIntegrationConfigSuccess,
  [A.getTerminalIntegrationConfigListSuccess]: getTerminalIntegrationConfigListSuccess,
  [A.createOrUpdateTerminalIntegrationConfigSuccess]: createOrUpdateTerminalIntegrationConfigSuccess,
  // task type
  [A.removeTaskTypeSuccess]: removeTaskTypeSuccess,
  [A.getTaskTypeListSuccess]: getTaskTypeListSuccess,
  [A.createOrUpdateTaskTypeSuccess]: createOrUpdateTaskTypeSuccess,
  // task status
  [A.removeTaskStatusSuccess]: removeTaskStatusSuccess,
  [A.getTaskStatusListSuccess]: getTaskStatusListSuccess,
  [A.createOrUpdateTaskStatusSuccess]: createOrUpdateTaskStatusSuccess,
  // transportation mode grouping
  [A.removeTransportationModeGroupingSuccess]: removeTransportationModeGroupingSuccess,
  [A.getTransportationModeGroupingListSuccess]: getTransportationModeGroupingListSuccess,
  [A.createOrUpdateTransportationModeGroupingSuccess]: createOrUpdateTransportationModeGroupingSuccess,
  // document sending config
  [A.removeDocumentSendingConfigSuccess]: removeDocumentSendingConfigSuccess,
  [A.getDocumentSendingConfigListSuccess]: getDocumentSendingConfigListSuccess,
  [A.createOrUpdateDocumentSendingConfigSuccess]: createOrUpdateDocumentSendingConfigSuccess,
  // expense types
  [A.removeExpenseTypeSuccess]: removeExpenseTypeSuccess,
  [A.getExpenseTypeListSuccess]: getExpenseTypeListSuccess,
  [A.createOrUpdateExpenseTypeSuccess]: createOrUpdateExpenseTypeSuccess,
  // cross border integration config
  [A.getCrossBorderIntegrationConfigSuccess]: getCrossBorderIntegrationConfigSuccess,
  [A.removeCrossBorderIntegrationConfigSuccess]: removeCrossBorderIntegrationConfigSuccess,
  [A.createOrUpdateCrossBorderIntegrationConfigSuccess]: createOrUpdateCrossBorderIntegrationConfigSuccess,
  // truck type mapping
  [A.removeTruckTypeMappingSuccess]: removeTruckTypeMappingSuccess,
  [A.getTruckTypeMappingListSuccess]: getTruckTypeMappingListSuccess,
  [A.createOrUpdateTruckTypeMappingSuccess]: createOrUpdateTruckTypeMappingSuccess,
  // trailer type mapping
  [A.removeTrailerTypeMappingSuccess]: removeTrailerTypeMappingSuccess,
  [A.getTrailerTypeMappingListSuccess]: getTrailerTypeMappingListSuccess,
  [A.createOrUpdateTrailerTypeMappingSuccess]: createOrUpdateTrailerTypeMappingSuccess,
  // dropdown options config
  [A.removeDropdownOptionConfigSuccess]: removeDropdownOptionConfigSuccess,
  [A.createOrUpdateDropdownOptionConfigSuccess]: createOrUpdateDropdownOptionConfigSuccess,
  // tabs
  [A.setIntegrationActiveConfigTab]: setActiveConfigTab,
  // equipment service auto create
  [A.setServiceTypeOptions]: setServiceTypeOptions,
  [A.setServiceVendorOptions]: setServiceVendorOptions,
  [A.removeEquipmentServiceAutoCreateConfigSuccess]: removeEquipmentServiceAutoCreateConfigSuccess,
  [A.getEquipmentServiceAutoCreateConfigListSuccess]: getEquipmentServiceAutoCreateConfigListSuccess,
  [A.createOrUpdateEquipmentServiceAutoCreateConfigSuccess]: createOrUpdateEquipmentServiceAutoCreateConfigSuccess,
  // reference type mapping
  [A.removeReferenceTypeMappingSuccess]: removeReferenceTypeMappingSuccess,
  [A.getReferenceTypeMappingListSuccess]: getReferenceTypeMappingListSuccess,
  [A.createOrUpdateReferenceTypeMappingSuccess]: createOrUpdateReferenceTypeMappingSuccess,
  // repair shop integration config
  [A.removeRepairShopIntegrationConfigSuccess]: removeRepairShopIntegrationConfigSuccess,
  [A.getRepairShopIntegrationConfigListSuccess]: getRepairShopIntegrationConfigListSuccess,
  [A.createOrUpdateRepairShopIntegrationConfigSuccess]: createOrUpdateRepairShopIntegrationConfigSuccess,
  // eta calculator
  [A.removeEtaCalculatorConfigSuccess]: removeEtaCalculatorConfigSuccess,
  [A.getEtaCalculatorConfigListSuccess]: getEtaCalculatorConfigListSuccess,
  [A.createOrUpdateEtaCalculatorConfigSuccess]: createOrUpdateEtaCalculatorConfigSuccess,
  // accessorial mapping config
  [A.removeAccessorialMappingSuccess]: removeAccessorialMappingSuccess,
  [A.getAccessorialMappingListSuccess]: getAccessorialMappingListSuccess,
  [A.createOrUpdateAccessorialMappingSuccess]: createOrUpdateAccessorialMappingSuccess,
  // container tracking config
  [A.removeContainerTrackingConfigSuccess]: removeContainerTrackingConfigSuccess,
  [A.getContainerTrackingConfigListSuccess]: getContainerTrackingConfigListSuccess,
  [A.createOrUpdateContainerTrackingConfigSuccess]: createOrUpdateContainerTrackingConfigSuccess,
  // crm integration config
  [A.removeCRMIntegrationConfigSuccess]: removeCRMIntegrationConfigSuccess,
  [A.getCRMIntegrationConfigListSuccess]: getCRMIntegrationConfigListSuccess,
  [A.createOrUpdateCRMIntegrationConfigSuccess]: createOrUpdateCRMIntegrationConfigSuccess,
  // service mapping config
  [A.removeServiceMappingByTypeSuccess]: removeServiceMappingByTypeSuccess,
  [A.getServiceMappingListByTypeSuccess]: getServiceMappingListByTypeSuccess,
  [A.createOrUpdateServiceMappingByTypeSuccess]: createOrUpdateServiceMappingByTypeSuccess,
  // order type mapping config
  [A.removeOrderTypeMappingByTypeSuccess]: removeOrderTypeMappingByTypeSuccess,
  [A.getOrderTypeMappingListByTypeSuccess]: getOrderTypeMappingListByTypeSuccess,
  [A.createOrUpdateOrderTypeMappingByTypeSuccess]: createOrUpdateOrderTypeMappingByTypeSuccess,
  // freight safety config
  [A.removeFreightSafetyConfigSuccess]: removeFreightSafetyConfigSuccess,
  [A.getFreightSafetyConfigListSuccess]: getFreightSafetyConfigListSuccess,
  [A.createOrUpdateFreightSafetyConfigSuccess]: createOrUpdateFreightSafetyConfigSuccess,
}, initialState);
